import React, { useState, useRef, useEffect, useMemo, useCallback, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import LoadingBar from "@components/ui/LoadingBar";
import { UserAvatar } from "@components/ui/UserAvatar";
import Icon1 from "@assets/icons/Icon";
import { useMessagerieStore } from "@store/messagerieStore";
import { messagerieApi } from "@services/api/messagerie";
import firebase from "@services/firebase";
import ForwardModal from "@components/chat/ForwardModal";
import { PollCard, PollComposerModal } from "@components/chat/Poll";
import { collection, query, orderBy, limit as fsLimit, onSnapshot } from "firebase/firestore";
import { useDataStore } from "@store/user_org";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import SearchField from "@components/ui/textfields/SearchField";
import SideMenuButton from "@navigation/SideMenuButton";
import { NotificationsBell, NotificationsPrompt } from "@components/notifications/NotificationsControl";
import BaseModal from "@components/modals/BaseModal";
import TextField from "@components/ui/textfields/TextField";
import Button from "@components/ui/buttons/Button";
import EntityChatPanel from "@components/chat/ChatPanel";
import { DocumentPreviewModal } from "@components/ui/DocumentPreviewModal";
import {
  getAttachments,
  isImageAtt,
  isAudioAtt,
  isPdfAtt,
  isVideoAtt,
  ImageAlbum,
  PdfCard,
  VideoCard,
  DaySeparator,
  msgDayKey,
  linkify,
  PinGlyph,
  PinnedBar,
  renderTextWithFolderTokens,
  useFolderSearch,
  FolderSearchPopover,
  type FolderRef,
} from "@components/chat/messageParts";
import type {
  DiscussionTab,
  ChatMember,
  Discussion,
  ChatMessage,
  ChatAttachment,
  ReadReceipt,
} from "../types/messaging";
import { includesNormalized } from "@utils/textSearch";

const AudioPlayer = lazy(() => import("@components/chat/AudioPlayer"));

// --- Constants ---

const EMPTY_READ_RECEIPTS: ReadReceipt[] = [];

const EMOJI_LIST = [
  "👍",
  "❤️",
  "😂",
  "🔥",
  "😍",
  "👏",
  "😮",
  "😢",
  "🙏",
  "💯",
  "✅",
  "🎉",
];

// --- Avatar helpers ---

// `radius` overrides the default circle — the thread uses rounded squares for
// sender avatars (flat rows), circles everywhere else.
function MemberAvatar({
  member,
  size = 24,
  radius,
}: {
  member: ChatMember;
  size?: number;
  radius?: number;
}) {
  // UserAvatar is shared app-wide and takes no style prop, so the rounded-square
  // variant is done with a wrapper rather than by widening its API.
  if (radius !== undefined) {
    return (
      <div className="flex-shrink-0 overflow-hidden" style={{ width: size, height: size, borderRadius: radius }}>
        <UserAvatar src={member.photo_url} value={member.avatar_seed || member.name} size={size} />
      </div>
    );
  }
  return (
    <UserAvatar src={member.photo_url} value={member.avatar_seed || member.name} size={size} className="rounded-full flex-shrink-0" />
  );
}

function AvatarStack({
  members,
  size = 24,
  maxVisible = 3,
  fixedWidth,
}: {
  members: ChatMember[];
  size?: number;
  maxVisible?: number;
  fixedWidth?: number;
}) {
  const visible = members.slice(0, maxVisible);
  const hiddenCount = members.length - maxVisible;
  const overlap = size * 0.58;

  return (
    <div
      className="flex flex-row items-center flex-shrink-0"
      style={fixedWidth ? { width: fixedWidth } : undefined}
    >
      {visible.map((m, i) => (
        <div
          key={m.id}
          className="flex-shrink-0"
          style={{
            marginLeft: i === 0 ? 0 : -overlap,
            zIndex: maxVisible - i + 1,
          }}
        >
          <MemberAvatar member={m} size={size} />
        </div>
      ))}
      {hiddenCount > 0 && (
        <span
          className="text-h2"
          style={{
            marginLeft: 2,
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "#A1A1A1",
          }}
        >
          +{hiddenCount}
        </span>
      )}
    </div>
  );
}

/** Titre d'une conversation : "OR-1183835 - NOM Client".
 *  Le numéro d'ordre seul ne dit pas de qui il s'agit, et le nom seul ne dit pas
 *  de quel passage au garage on parle — la liste et l'en-tête du fil ouvert
 *  affichent donc la même chose. */
export function discussionTitle(d: { ordre_nums?: string[]; name: string }): string {
  const nums = d.ordre_nums || [];
  return nums.length > 0 ? `${nums.join(", ")} - ${d.name}` : d.name;
}

// --- Discussion list item ---

function DiscussionItem({ discussion }: { discussion: Discussion }) {
  const selectedId = useMessagerieStore((s) => s.selectedDiscussionId);
  const setSelected = useMessagerieStore((s) => s.setSelectedDiscussion);
  const members = useMessagerieStore((s) => s.members);
  const isSelected = selectedId === discussion.id;

  // Read unread status from store (not prop) so it updates when cleared
  const clientUnread = useMessagerieStore((s) => (s.unreadCounts[discussion.id] || 0) > 0);

  // Check if the sibling internal discussion has unreads
  const internalUnread = useMessagerieStore((s) => {
    if (discussion.type === "internal") return 0;
    const entityId = discussion.ordre_id || discussion.dossier_id;
    if (!entityId) return 0;
    const entityKey = discussion.ordre_id ? "ordre_id" : "dossier_id";
    const sibling = s.discussions.find((d) =>
      d.type === "internal" && d.id !== discussion.id &&
      (entityKey === "ordre_id" ? d.ordre_id === entityId : d.dossier_id === entityId)
    );
    return sibling ? (s.unreadCounts[sibling.id] || 0) : 0;
  });
  const hasUnread = clientUnread || internalUnread > 0;

  const discMembers = members.filter((m) => discussion.members.includes(m.id));

  // Title = "OR-1183835 - NOM Client", subtitle = the last message in grey.
  // The OR number alone said nothing about who it is, and the client name was
  // pushed into the subtitle, which hid the conversation itself.
  const titleText = discussionTitle(discussion);
  const subtitleText = discussion.lastMessage;

  return (
    <button
      onClick={() => setSelected(discussion.id)}
      className="flex flex-row items-center w-full text-left"
      style={{
        padding: 10,
        gap: 25,
        height: 56,
        borderRadius: 10,
        background: isSelected ? "#F5F5F5" : "transparent",
      }}
    >
      <AvatarStack
        members={discMembers}
        size={28}
        maxVisible={3}
        fixedWidth={55}
      />
      <div className="flex flex-col flex-1" style={{ minWidth: 0 }}>
        <div className="flex flex-row justify-between items-end">
          <span
            className="text-h1"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              lineHeight: "21px",
              color: "#000000",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {titleText}
          </span>
          <span
            className="text-h4 text-neutral-400"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              lineHeight: "18px",
              flexShrink: 0,
            }}
          >
            {discussion.lastMessageTime}
          </span>
        </div>
        <div
          className="flex flex-row justify-between items-start"
          style={{ gap: 5 }}
        >
          <span
            className="text-h3 text-neutral-400"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              lineHeight: "15px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {subtitleText}
          </span>
          {hasUnread && (
            <div
              className="flex items-center justify-center"
              style={{ width: 29, flexShrink: 0, gap: 3 }}
            >
              {clientUnread && (
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: "#6366f1",
                  }}
                />
              )}
              {internalUnread > 0 && (
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: "#525252",
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

// --- Discussions panel (left) ---

// « Interne » réunit les fils d'équipe (générales) et les conversations privées :
// deux façons de se parler entre collègues, qu'on ne veut pas chercher dans deux
// onglets. Le choix générales / privée reste posé à la création d'une discussion.
const TAB_OPTIONS: { id: DiscussionTab; label: string }[] = [
  { id: "interne", label: "Interne" },
  { id: "ordres", label: "Dossiers" },
];

function DiscussionSkeleton() {
  return (
    <div className="flex flex-row items-center" style={{ padding: "10px 8px", gap: 12 }}>
      <div className="w-10 h-10 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
      <div className="flex flex-col flex-1 gap-1.5">
        <div className="h-3.5 w-32 rounded bg-neutral-200 animate-pulse" />
        <div className="h-3 w-48 rounded bg-neutral-100 animate-pulse" />
      </div>
      <div className="h-3 w-10 rounded bg-neutral-100 animate-pulse flex-shrink-0" />
    </div>
  );
}

function DiscussionsPanel() {
  const activeTab = useMessagerieStore((s) => s.activeTab);
  const setActiveTab = useMessagerieStore((s) => s.setActiveTab);
  const searchQuery = useMessagerieStore((s) => s.searchQuery);
  const setSearchQuery = useMessagerieStore((s) => s.setSearchQuery);
  const isLoadingDiscussions = useMessagerieStore((s) => s.isLoadingDiscussions);
  const isLoadingMoreDiscussions = useMessagerieStore((s) => s.isLoadingMoreDiscussions);
  const hasMoreDiscussions = useMessagerieStore((s) => s.hasMoreDiscussions);
  const fetchMoreDiscussions = useMessagerieStore((s) => s.fetchMoreDiscussions);
  const getFilteredDiscussions = useMessagerieStore(
    (s) => s.getFilteredDiscussions,
  );

  // Subscribe to unreadCounts + discussions so tab dots re-render on change
  const unreadCounts = useMessagerieStore((s) => s.unreadCounts);
  const allDiscussions = useMessagerieStore((s) => s.discussions);
  const getUnreadCount = useMessagerieStore((s) => s.getUnreadCount);

  const ensureUnreadDiscussionsLoaded = useMessagerieStore(
    (s) => s.ensureUnreadDiscussionsLoaded,
  );

  const [showNewDiscussion, setShowNewDiscussion] = useState(false);

  // L'onglet « Dossiers » n'affiche que les fils non lus : il faut donc que les
  // discussions non lues restées au-delà de la page courante soient chargées.
  useEffect(() => {
    if (activeTab === "ordres") ensureUnreadDiscussionsLoaded();
  }, [activeTab, unreadCounts, ensureUnreadDiscussionsLoaded]);

  // Pendant une recherche, la liste filtrée est trop courte pour défiler, donc la
  // pagination au scroll ne se déclenche plus : on charge quelques pages de plus
  // pour que la recherche porte sur autre chose que la première page.
  useEffect(() => {
    if (searchQuery.trim().length < 2) return;
    if (!hasMoreDiscussions || isLoadingMoreDiscussions) return;
    if (allDiscussions.length >= 300) return;
    fetchMoreDiscussions();
  }, [
    searchQuery,
    hasMoreDiscussions,
    isLoadingMoreDiscussions,
    allDiscussions.length,
    fetchMoreDiscussions,
  ]);

  const discussions = getFilteredDiscussions();

  // La liste est paginée par dernière activité côté serveur, et la synchro touche
  // des fils jamais utilisés : une page de 25 peut n'en montrer que 5 une fois les
  // fils vides masqués. On charge quelques pages de plus pour remplir la liste.
  useEffect(() => {
    if (discussions.length >= 20) return;
    if (!hasMoreDiscussions || isLoadingMoreDiscussions) return;
    if (allDiscussions.length >= 300) return;
    fetchMoreDiscussions();
  }, [
    discussions.length,
    hasMoreDiscussions,
    isLoadingMoreDiscussions,
    allDiscussions.length,
    fetchMoreDiscussions,
  ]);

  const tabs = TAB_OPTIONS.map((t) => {
    const count = getUnreadCount(t.id);
    return {
      value: t.id,
      label: t.label,
      badge: count,
    };
  });

  return (
    <div
      className="flex flex-col flex-shrink-0"
      style={{
        width: 380,
        height: "100%",
        padding: "19px 20px 23px",
        gap: 25,
        background: "#FFFFFF",
        borderRight: "1px solid #DDDDDD",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      <div
        className="flex flex-row items-center justify-between"
        style={{ padding: "0 8px" }}
      >
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 500,
            fontSize: 16,
            lineHeight: "24px",
            color: "#000000",
          }}
        >
          Discussions
        </span>
        <div className="flex flex-row items-center" style={{ gap: 2 }}>
          <NotificationsBell />
          <button
            onClick={() => setShowNewDiscussion(true)}
            className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
            style={{ width: 32, height: 32 }}
          >
            <Icon1 name="plus" size={14} color="#6366f1" />
          </button>
        </div>
      </div>
      {showNewDiscussion && (
        <NewDiscussionModal onClose={() => setShowNewDiscussion(false)} />
      )}

      <ChunkyTabs
        activeTab={activeTab}
        tabs={tabs}
        setActiveTab={(tab) => setActiveTab(tab as DiscussionTab)}
      />

      <SearchField
        value={searchQuery}
        onChange={(v: string) => setSearchQuery(v)}
        placeholder="Chercher .."
      />

      <NotificationsPrompt />

      <div
        className="flex flex-col flex-1"
        style={{ overflowY: "auto", minHeight: 0 }}
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            fetchMoreDiscussions();
          }
        }}
      >
        {isLoadingDiscussions && discussions.length === 0 ? (
          <>
            {Array.from({ length: 8 }).map((_, i) => (
              <DiscussionSkeleton key={i} />
            ))}
          </>
        ) : (
          <>
            {discussions.map((d) => (
              <DiscussionItem key={d.id} discussion={d} />
            ))}
            {isLoadingMoreDiscussions && (
              <div className="flex flex-col gap-0">
                {Array.from({ length: 3 }).map((_, i) => (
                  <DiscussionSkeleton key={`more-${i}`} />
                ))}
              </div>
            )}
            {discussions.length === 0 && (
              <div className="flex flex-col items-center text-center gap-1 py-10 px-6">
                <Icon1 name="check" size={20} color="#CCCCCC" />
                <span className="text-h3 text-neutral-400">
                  {searchQuery ? "Aucun résultat" : "Aucune conversation"}
                </span>
                <span className="text-xs text-neutral-300">
                  {searchQuery
                    ? "Essayez un nom de client, une plaque ou un numéro d'OR."
                    : "Les conversations sont triées par dernier message."}
                </span>
              </div>
            )}
            {!hasMoreDiscussions && discussions.length > 0 && (
              <div className="text-center text-xs text-neutral-300 py-3">
                {discussions.length}{" "}
                {`conversation${discussions.length > 1 ? "s" : ""}`}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- New discussion modal ---

function NewDiscussionModal({ onClose }: { onClose: () => void }) {
  const members = useMessagerieStore((s) => s.members);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const createDiscussion = useMessagerieStore((s) => s.createDiscussion);

  const [name, setName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [tab, setTab] = useState<DiscussionTab>("generales");
  const [memberSearch, setMemberSearch] = useState("");

  const otherMembers = members.filter((m) => m.id !== currentUserId);
  const filteredMembers = memberSearch
    ? otherMembers.filter((m) => includesNormalized(m.name, memberSearch))
    : otherMembers;

  const toggleMember = (id: string) => {
    setSelectedMembers((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    );
  };

  const handleCreate = () => {
    if (!name.trim() || selectedMembers.length === 0) return;
    createDiscussion(name.trim(), selectedMembers, tab);
    onClose();
  };

  return (
    <BaseModal
      onClose={onClose}
      title="Nouvelle discussion"
      startIcon={<Icon1 name="side-messagerie" size={20} />}
      width="max-w-md"
    >
      <div className="flex flex-col gap-4 p-4">
        <TextField
          label="Nom"
          value={name}
          onChange={(v) => setName(v)}
          placeholder="Nom de la discussion"
        />

        {/* Tab */}
        <div className="flex flex-col gap-1">
          <span className="text-h4 text-neutral-400">Catégorie</span>
          <div className="flex flex-row gap-2">
            {(
              [
                { id: "generales", label: "Générales" },
                { id: "privees", label: "Privées" },
              ] as { id: DiscussionTab; label: string }[]
            ).map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`text-h3 font-medium px-3 py-1.5 rounded-lg transition-colors ${
                  tab === t.id
                    ? "bg-indigo-500 text-white"
                    : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Members */}
        <div className="flex flex-col gap-1">
          <span className="text-h4 text-neutral-400">
            Membres ({selectedMembers.length})
          </span>
          <SearchField
            value={memberSearch}
            onChange={(v) => setMemberSearch(v)}
            placeholder="Rechercher un membre"
          />
          <div
            className="flex flex-col mt-1"
            style={{ maxHeight: 200, overflowY: "auto", gap: 2 }}
          >
            {filteredMembers.map((m) => {
              const selected = selectedMembers.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleMember(m.id)}
                  className={`flex flex-row items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 ${
                    selected ? "bg-indigo-50" : ""
                  }`}
                >
                  <MemberAvatar member={m} size={28} />
                  <span className="text-h3 font-medium text-black flex-1 text-left">
                    {m.name}
                  </span>
                  {selected && (
                    <Icon1 name="check" size={14} color="#6366f1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          label="Créer"
          onClick={handleCreate}
          disabled={!name.trim() || selectedMembers.length === 0}
          variant="large"
        />
      </div>
    </BaseModal>
  );
}

// --- Mention text renderer ---

function RenderMessageText({
  text,
  mentions,
  folderRefs,
  isOwn,
}: {
  text: string;
  mentions?: string[];
  folderRefs?: { id: string; name: string }[];
  isOwn?: boolean;
}) {
  const getMember = useMessagerieStore((s) => s.getMember);

  // Link color: white on the black own-bubble, indigo on the light other-bubble.
  const linkColor = isOwn ? "#FFFFFF" : "#4F46E5";

  // Renders a plain chunk: linkify + clickable /Name folder chips.
  const renderChunk = (chunk: string) => renderTextWithFolderTokens(chunk, folderRefs, linkColor, false, isOwn);

  // Build a regex that matches @MemberName for mentioned members
  const mentionNames = (mentions || [])
    .map((id) => getMember(id))
    .filter(Boolean)
    .map((m) => m!.name);

  if (mentionNames.length === 0) return <>{renderChunk(text)}</>;

  const pattern = new RegExp(
    `(@(?:${mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}))`,
    "g",
  );

  const parts = text.split(pattern);

  return (
    <>
      {parts.map((part, i) => {
        if (
          part.startsWith("@") &&
          mentionNames.some((n) => part === `@${n}`)
        ) {
          return (
            <span key={i} className="font-semibold text-indigo-500">
              {part}
            </span>
          );
        }
        return <React.Fragment key={i}>{renderChunk(part)}</React.Fragment>;
      })}
    </>
  );
}

// --- Emoji picker ---

function EmojiPicker({
  onSelect,
  onClose,
  alignRight,
}: {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  alignRight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        bottom: "100%",
        ...(alignRight ? { right: 0 } : { left: 0 }),
        marginBottom: 4,
        padding: 6,
        background: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: 10,
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 2,
      }}
    >
      {EMOJI_LIST.map((emoji) => (
        <button
          key={emoji}
          onClick={() => onSelect(emoji)}
          className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
          style={{ width: 32, height: 32, fontSize: 18 }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

// --- Reaction pill with tooltip ---

function ReactionPill({
  emoji,
  userIds,
  isMine,
  onToggle,
}: {
  emoji: string;
  userIds: string[];
  isMine: boolean;
  onToggle: () => void;
}) {
  const getMember = useMessagerieStore((s) => s.getMember);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const names = userIds
    .map((id) => getMember(id))
    .filter(Boolean)
    .map((m) => m!.name);

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        if (btnRef.current) {
          const r = btnRef.current.getBoundingClientRect();
          setTooltipPos({ x: r.left + r.width / 2, y: r.top - 4 });
        }
      }}
      onMouseLeave={() => setTooltipPos(null)}
    >
      {tooltipPos && createPortal(
        <div
          style={{
            position: "fixed",
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: "translate(-50%, -100%)",
            zIndex: 9999,
            padding: "4px 8px",
            background: "#333333",
            borderRadius: 6,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: 12,
              color: "#FFFFFF",
            }}
          >
            {names.join(", ")}
          </span>
        </div>,
        document.body
      )}
      <button
        ref={btnRef}
        onClick={onToggle}
        className="flex items-center justify-center"
        style={{
          height: 26,
          padding: "0 9px",
          gap: 5,
          background: isMine ? "#EEF0FF" : "#FFFFFF",
          border: `1px solid ${isMine ? "#C7CCF5" : "#E5E7EB"}`,
          borderRadius: 999,
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          cursor: "pointer",
        }}
      >
        <span style={{ fontSize: 14, lineHeight: 1 }}>{emoji}</span>
        {userIds.length > 1 && (
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 600,
              fontSize: 12,
              color: isMine ? "#464DFF" : "#6B7280",
            }}
          >
            {userIds.length}
          </span>
        )}
      </button>
    </div>
  );
}

// --- Bubble radius helpers (WhatsApp style) ---

type BubblePosition = "solo" | "first" | "middle" | "last";

// --- Flat-thread design tokens (same values as the hexagone thread) --------
// Received messages render flat: rounded-square avatar, "Nom · heure" header,
// bare text on the page background — no bubble. Our own keep the black bubble,
// which is also where the read ticks live.
const T = {
  avatar: 40,
  avatarRadius: 12,
  gutter: 12,
  name: "#111827",
  body: "#1F2328",
  meta: "#8A8F98",
  hairline: "#ECECEC",
  chipBorder: "#E5E7EB",
  chipBg: "#FFFFFF",
  mineBg: "#EEF0FF",
  mineBorder: "#C7CCF5",
  mineText: "#464DFF",
  quoteRule: "#C7CCF5",
  accent: "#6366f1",
  maxMsg: 680,
  // Room kept free beside a message for the hover actions.
  toolbarGutter: 160,
};

function getBubbleRadius(isOwn: boolean, position: BubblePosition): string {
  if (position === "solo") return isOwn ? "10px 10px 4px 10px" : "10px 10px 10px 4px";
  if (isOwn) {
    if (position === "first") return "10px 10px 4px 10px";
    if (position === "middle") return "10px 4px 4px 10px";
    return "10px 4px 10px 10px"; // last
  }
  // other
  if (position === "first") return "10px 10px 10px 4px";
  if (position === "middle") return "4px 10px 10px 4px";
  return "4px 10px 10px 10px"; // last
}

// --- Message group type ---

interface MessageGroupData {
  senderId: string;
  messages: ChatMessage[];
  isOwn: boolean;
}

function groupMessages(messages: ChatMessage[], currentUserId: string): MessageGroupData[] {
  const groups: MessageGroupData[] = [];
  for (const msg of messages) {
    const sid = msg.sender_id || msg.senderId || "";
    const lastGroup = groups[groups.length - 1];
    // Break group on deleted messages, different sender, or a calendar-day change
    const dayChanged = lastGroup && msgDayKey(lastGroup.messages[0]) !== msgDayKey(msg);
    if (msg.deleted || !lastGroup || lastGroup.senderId !== sid || lastGroup.messages.some(m => m.deleted) || dayChanged) {
      groups.push({ senderId: sid, messages: [msg], isOwn: sid === currentUserId });
    } else {
      lastGroup.messages.push(msg);
    }
  }
  return groups;
}

// --- Chat message bubble (WhatsApp style) ---

// --- Read ticks ---

function ReadTicks({ status }: { status: "sent" | "read_some" | "read_all" | "delivered" | "read" | "failed" }) {
  if (status === "failed") {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
        <path d="M2 2L10 10M10 2L2 10" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (status === "sent") {
    return (
      <svg width="12" height="9" viewBox="0 0 12 9" fill="none" style={{ flexShrink: 0 }}>
        <path d="M1 4.5L4 7.5L10.5 1" stroke="#A3A3A3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  const color = (status === "read_all" || status === "read") ? "#34B7F1" : "#A3A3A3";
  return (
    <svg width="17" height="9" viewBox="0 0 17 9" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1 4.5L4 7.5L10.5 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.5 4.5L8.5 7.5L15 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// --- Read info panel ---

function ReadInfoPanel({
  msgIndex,
  messages,
  readReceipts,
  allMemberIds,
  currentUserId,
  onClose,
}: {
  msgIndex: number;
  messages: ChatMessage[];
  readReceipts: ReadReceipt[];
  allMemberIds: string[];
  currentUserId: string;
  onClose: () => void;
}) {
  const storeMembers = useMessagerieStore((s) => s.members);
  const getReadTimeForMessage = useMessagerieStore((s) => s.getReadTimeForMessage);
  const otherMemberIds = allMemberIds.filter((id) => id !== currentUserId);
  const targetMessage = msgIndex >= 0 ? messages[msgIndex] : undefined;

  const { readers, notRead } = useMemo(() => {
    const readers: { name: string; time: string; photoUrl?: string; avatarSeed?: string }[] = [];
    const notRead: { name: string; photoUrl?: string; avatarSeed?: string }[] = [];
    for (const memberId of otherMemberIds) {
      const member = storeMembers.find((m) => m.id === memberId);
      const memberName = member?.name || "Inconnu";
      const photoUrl = member?.photo_url;
      const avatarSeed = member?.avatar_seed;
      const receipt = readReceipts.find((r) => r.user_id === memberId);
      if (receipt) {
        const receiptIndex = messages.findIndex((m) => m.id === receipt.last_read_message_id);
        if (receiptIndex >= msgIndex) {
          // Checkpoint-accurate read time for this specific message.
          const readAt = targetMessage
            ? getReadTimeForMessage(receipt, targetMessage, messages)
            : receipt.last_read_at;
          const d = new Date(readAt || receipt.last_read_at);
          const time = isNaN(d.getTime()) ? "" : d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
          readers.push({ name: memberName, time, photoUrl, avatarSeed });
        } else {
          notRead.push({ name: memberName, photoUrl, avatarSeed });
        }
      } else {
        notRead.push({ name: memberName, photoUrl, avatarSeed });
      }
    }
    return { readers, notRead };
  }, [msgIndex, messages, readReceipts, otherMemberIds, storeMembers, targetMessage, getReadTimeForMessage]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.35)" }}
    >
      <div
        className="flex flex-col rounded-2xl"
        style={{ background: "#FFFFFF", border: "1px solid #E5E5E5", maxHeight: "60vh", overflow: "hidden", width: 320 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row items-center justify-between" style={{ padding: "14px 16px 10px", borderBottom: "1px solid #E5E5E5" }}>
          <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: "#1E1E1E" }}>Infos du message</span>
          <button onClick={onClose} className="flex items-center justify-center" style={{ width: 24, height: 24, borderRadius: 6 }}>
            <Icon1 name="close" size={14} color="#6B7280" />
          </button>
        </div>
        <div className="overflow-y-auto" style={{ padding: "10px 0 16px" }}>
          {readers.length > 0 && (
            <div>
              <div className="flex flex-row items-center" style={{ padding: "4px 16px 6px", gap: 6 }}>
                <svg width="15" height="10" viewBox="0 0 17 9" fill="none">
                  <path d="M1 4.5L4 7.5L10.5 1" stroke="#34B7F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M5.5 4.5L8.5 7.5L15 1" stroke="#34B7F1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: "#34B7F1", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Lu par {readers.length}
                </span>
              </div>
              {readers.map((r, i) => (
                <div key={i} className="flex flex-row items-center justify-between" style={{ padding: "6px 16px" }}>
                  <div className="flex flex-row items-center" style={{ gap: 10 }}>
                    <UserAvatar src={r.photoUrl} value={r.avatarSeed || r.name} size={32} className="w-8 h-8 rounded-full" />
                    <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>{r.name}</span>
                  </div>
                  {r.time && <span style={{ fontFamily: "Poppins", fontSize: 11, color: "#6B7280" }}>{r.time}</span>}
                </div>
              ))}
            </div>
          )}
          {notRead.length > 0 && (
            <div style={{ marginTop: readers.length > 0 ? 8 : 0 }}>
              <div className="flex flex-row items-center" style={{ padding: "4px 16px 6px", gap: 6 }}>
                <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                  <path d="M1 4.5L4 7.5L10.5 1" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Pas encore lu {notRead.length}
                </span>
              </div>
              {notRead.map((r, i) => (
                <div key={i} className="flex flex-row items-center" style={{ padding: "6px 16px", gap: 10 }}>
                  <UserAvatar src={r.photoUrl} value={r.avatarSeed || r.name} size={32} className="w-8 h-8 rounded-full" />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#6B7280" }}>{r.name}</span>
                </div>
              ))}
            </div>
          )}
          {otherMemberIds.length === 0 && (
            <div className="flex items-center justify-center py-6">
              <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#6B7280" }}>Conversation privée</span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

type MessageBubbleProps = {
  message: ChatMessage;
  position: BubblePosition;
  showTimestamp: boolean;
  readStatus?: "sent" | "read_some" | "read_all" | "delivered" | "read" | "failed";
  onReadInfoClick?: (msgId: string) => void;
  /** Square thumbnail inside a run of photo-only messages. */
  compact?: boolean;
};

// ─── Bubble memo comparators ────────────────────────────────
// Cheap structural checks for the in-place-updating arrays so React.memo can
// skip a bubble re-render when no render-affecting data prop changed. Note that
// isPinned / uploadProgress / replySource are read from the store via hooks
// inside the bubble, so they still update live regardless of this comparator.

function reactionsEqual(a: ChatMessage["reactions"], b: ChatMessage["reactions"]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].emoji !== b[i].emoji ||
      (a[i].user_id || a[i].userId || "") !== (b[i].user_id || b[i].userId || "")
    ) return false;
  }
  return true;
}

function attachmentsEqual(a?: ChatAttachment[], b?: ChatAttachment[]): boolean {
  if (a === b) return true;
  const la = a?.length || 0;
  const lb = b?.length || 0;
  if (la !== lb) return false;
  for (let i = 0; i < la; i++) {
    const x = a![i];
    const y = b![i];
    if (x === y) continue;
    if (
      x.url !== y.url ||
      x.base64Data !== y.base64Data ||
      x.name !== y.name ||
      x.preview_url !== y.preview_url ||
      (x.mime_type || x.mimeType) !== (y.mime_type || y.mimeType)
    ) return false;
  }
  return true;
}

function folderRefsEqual(a?: { id: string; name: string }[], b?: { id: string; name: string }[]): boolean {
  if (a === b) return true;
  const la = a?.length || 0;
  const lb = b?.length || 0;
  if (la !== lb) return false;
  for (let i = 0; i < la; i++) {
    if (a![i].id !== b![i].id || a![i].name !== b![i].name) return false;
  }
  return true;
}

function stringArrayEqual(a?: string[], b?: string[]): boolean {
  if (a === b) return true;
  const la = a?.length || 0;
  const lb = b?.length || 0;
  if (la !== lb) return false;
  for (let i = 0; i < la; i++) if (a![i] !== b![i]) return false;
  return true;
}

/** True when two store messages are identical for every render-affecting field. */
function chatMessageEqual(a: ChatMessage, b: ChatMessage): boolean {
  if (a === b) return true;
  return (
    a.id === b.id &&
    a.text === b.text &&
    a.edited === b.edited &&
    a.deleted === b.deleted &&
    a.timestamp === b.timestamp &&
    a.created_at === b.created_at &&
    (a.sender_id || a.senderId || "") === (b.sender_id || b.senderId || "") &&
    a.sender_name === b.sender_name &&
    (a.reply_to_id || a.replyToId) === (b.reply_to_id || b.replyToId) &&
    a.channel === b.channel &&
    a.wa_status === b.wa_status &&
    a.email_status === b.email_status &&
    a.event === b.event &&
    reactionsEqual(a.reactions, b.reactions) &&
    attachmentsEqual(getAttachments(a), getAttachments(b)) &&
    folderRefsEqual(a.folder_refs, b.folder_refs) &&
    stringArrayEqual(a.mentions, b.mentions)
  );
}

/** Memo comparator: re-render (return false) when ANY render-affecting data prop
 * changes. `onReadInfoClick` is intentionally NOT compared (host passes a stable
 * useCallback ref). */
function messageBubbleAreEqual(prev: MessageBubbleProps, next: MessageBubbleProps): boolean {
  return (
    prev.position === next.position &&
    prev.showTimestamp === next.showTimestamp &&
    prev.readStatus === next.readStatus &&
    prev.compact === next.compact &&
    chatMessageEqual(prev.message, next.message)
  );
}

function MessageBubbleImpl({
  message,
  compact,
  position,
  showTimestamp,
  readStatus,
  onReadInfoClick,
}: MessageBubbleProps) {
  const getMember = useMessagerieStore((s) => s.getMember);
  const getMessageById = useMessagerieStore((s) => s.getMessageById);
  const toggleReaction = useMessagerieStore((s) => s.toggleReaction);
  const setReplyingTo = useMessagerieStore((s) => s.setReplyingTo);
  const deleteMessage = useMessagerieStore((s) => s.deleteMessage);
  const updateMessageIn = useMessagerieStore((s) => s.updateMessageIn);
  const selectedDiscussionId = useMessagerieStore((s) => s.selectedDiscussionId);
  const votePoll = useMessagerieStore((s) => s.votePoll);
  const closePoll = useMessagerieStore((s) => s.closePoll);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const msgUploadProgress = useMessagerieStore((s) => s.uploadProgress[message.id]);
  const pinMessage = useMessagerieStore((s) => s.pinMessage);
  const unpinMessage = useMessagerieStore((s) => s.unpinMessage);
  const setPendingScrollToMessageId = useMessagerieStore((s) => s.setPendingScrollToMessageId);
  const isPinned = useMessagerieStore((s) =>
    !!(selectedDiscussionId &&
      s.discussions.find((d) => d.id === selectedDiscussionId)?.pinned_messages?.some((p) => p.message_id === message.id))
  );

  const [hovered, setHovered] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [fullPickerAnchor, setFullPickerAnchor] = useState<{ x: number; y: number } | null>(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  // Gallery lightbox: list of attachments + active index, scoped to this message.
  const [lightbox, setLightbox] = useState<{ list: ChatAttachment[]; idx: number } | null>(null);
  const openLightbox = (list: ChatAttachment[], att: ChatAttachment) =>
    setLightbox({ list, idx: Math.max(0, list.indexOf(att)) });
  const lightboxStep = (delta: number) =>
    setLightbox((lb) => (lb ? { ...lb, idx: (lb.idx + delta + lb.list.length) % lb.list.length } : lb));
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!showDropdown) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showDropdown]);

  // Position the dropdown portal after it renders so we can measure its actual width
  useEffect(() => {
    if (!showDropdown || !triggerRef.current) {
      setDropdownPos(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    // Defer to next frame so dropdownRef is mounted and measurable
    requestAnimationFrame(() => {
      const menuWidth = dropdownRef.current?.offsetWidth || 260;
      const menuHeight = dropdownRef.current?.offsetHeight || 200;
      const top = rect.bottom + 4 + menuHeight > window.innerHeight
        ? rect.top - menuHeight - 4
        : rect.bottom + 4;
      const spaceRight = window.innerWidth - rect.left;
      const left = spaceRight >= menuWidth ? rect.left : rect.right - menuWidth;
      setDropdownPos({ top, left: Math.max(4, left) });
    });
  }, [showDropdown]);

  useEffect(() => {
    if (isEditing) editInputRef.current?.focus();
  }, [isEditing]);

  const msgSenderId = message.sender_id || message.senderId || "";
  const sender = getMember(msgSenderId);
  if (!sender) return null;

  const isOwn = msgSenderId === currentUserId;

  // Group reactions by emoji
  const groupedReactions = message.reactions.reduce(
    (acc, r) => {
      const uid = r.user_id || r.userId || "";
      if (!acc[r.emoji]) acc[r.emoji] = [];
      acc[r.emoji].push(uid);
      return acc;
    },
    {} as Record<string, string[]>,
  );
  const hasReactions = Object.keys(groupedReactions).length > 0;

  // Reply source
  const msgReplyToId = message.reply_to_id || message.replyToId;
  const replySource = msgReplyToId
    ? getMessageById(msgReplyToId)
    : undefined;
  const replySender = replySource ? getMember(replySource.sender_id || replySource.senderId || "") : undefined;

  // Download handler
  const handleDownload = async (url?: string, base64?: string, name?: string) => {
    const href = base64 || url;
    if (!href) {
      alert(`Téléchargement simulé : ${name || "fichier"}`);
      return;
    }
    try {
      const res = await fetch(href);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(href, "_blank");
    }
  };

  // Split attachments into images / audio / pdf / other so a single message
  // renders every file (album + audio players + pdf cards + file rows).
  const attachments = getAttachments(message);
  const imageAtts = attachments.filter(isImageAtt);
  const audioAtts = attachments.filter(isAudioAtt);
  const videoAtts = attachments.filter(isVideoAtt);
  const docAtts = attachments.filter((a) => !isImageAtt(a) && !isAudioAtt(a) && !isVideoAtt(a));
  const downloadAtt = (a: ChatAttachment) =>
    handleDownload(a.url, a.base64Data, a.name || (isImageAtt(a) ? "image.png" : isPdfAtt(a) ? "document.pdf" : isVideoAtt(a) ? "video.mp4" : "fichier"));

  // 60-minute edit window check
  const canEdit = isOwn && !message.deleted && !!message.created_at &&
    (Date.now() - new Date(message.created_at).getTime()) < 60 * 60 * 1000;

  const startEditing = () => {
    setEditText(message.text || "");
    setIsEditing(true);
    setShowDropdown(false);
    setHovered(false);
  };

  const saveEdit = () => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== message.text && selectedDiscussionId) {
      updateMessageIn(selectedDiscussionId, message.id, trimmed);
    }
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditText("");
  };

  // Bubble colors
  // Received messages render flat (no bubble); our own keep the black bubble.
  const flat = !isOwn;
  const bubbleBg = isOwn ? "#000000" : "#F5F5F5";
  const bubbleRadius = getBubbleRadius(isOwn, position);

  // --- Deleted message ---
  if (message.deleted) {
    return (
      <div
        id={`chat-msg-${message.id}`}
        style={
          flat
            ? { padding: "2px 0", opacity: 0.7 }
            : { padding: "8px 12px", background: bubbleBg, borderRadius: bubbleRadius, maxWidth: "65%", opacity: 0.7 }
        }
      >
        <span
          className="text-h2 italic text-neutral-400"
          style={{ fontFamily: "Poppins" }}
        >
          Ce message a été supprimé
        </span>
      </div>
    );
  }

  // --- Normal message ---
  return (
    <div
      id={`chat-msg-${message.id}`}
      className="relative"
      style={{
        ...(flat
          ? { width: "fit-content", maxWidth: `min(${T.maxMsg}px, calc(100% - ${T.toolbarGutter}px))` }
          : { minWidth: 80, width: "fit-content" }),
        borderRadius: 12,
        transition: "outline 0.2s, background 0.2s",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { if (!showDropdown) setHovered(false); }}
    >
      {/* Dropdown trigger */}
      {(hovered || showDropdown) && (
        <div
          className="absolute z-20"
          style={
            flat
              ? { top: -3, left: "100%", paddingLeft: 8 }
              : { top: -3, right: "100%", paddingRight: 8 }
          }
        >
          <button
            ref={triggerRef}
            onClick={() => setShowDropdown((v) => !v)}
            className="flex items-center justify-center hover:bg-neutral-100"
            style={{
              width: 28, height: 28, borderRadius: 8,
              background: "#FFFFFF",
              border: `1px solid ${T.chipBorder}`,
              boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
              cursor: "pointer",
            }}
          >
            <svg width="12" height="7" viewBox="0 0 10 6" fill="none">
              <path d="M1 1L5 5L9 1" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          className="flex flex-col"
          style={{
            position: "fixed",
            zIndex: 9999,
            top: dropdownPos?.top ?? -9999,
            left: dropdownPos?.left ?? -9999,
            opacity: dropdownPos ? 1 : 0,
            background: "#FFFFFF",
            boxShadow: "0px 6px 20px rgba(0,0,0,0.12)",
            borderRadius: 10,
            border: "1px solid #F0F0F0",
            overflow: "hidden",
            minWidth: 190,
          }}
        >
          {/* Quick emoji row */}
          <div className="flex flex-row items-center" style={{ padding: "8px 8px 6px", gap: 1, borderBottom: "1px solid #F5F5F5" }}>
            {EMOJI_LIST.slice(0, 7).map((emoji) => (
              <button
                key={emoji}
                onClick={() => { toggleReaction(message.id, emoji); setShowDropdown(false); setHovered(false); }}
                className="hover:bg-neutral-100 rounded-lg flex items-center justify-center"
                style={{ width: 30, height: 30, fontSize: 17, flexShrink: 0 }}
              >
                {emoji}
              </button>
            ))}
            <button
              onClick={(e) => {
                const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                const x = Math.min(r.left, window.innerWidth - 360);
                const y = r.bottom + 6;
                setFullPickerAnchor({ x, y });
                setShowDropdown(false);
              }}
              className="hover:bg-neutral-100 rounded-lg flex items-center justify-center"
              style={{ width: 30, height: 30, fontSize: 15, flexShrink: 0, color: "#737373" }}
            >
              +
            </button>
          </div>

          {/* Actions */}
          <button
            onClick={() => { setForwardOpen(true); setShowDropdown(false); setHovered(false); }}
            className="flex items-center hover:bg-neutral-50 w-full"
            style={{ padding: "9px 14px", gap: 10 }}
          >
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none"><path d="M9 4l4 4-4 4M13 8H3.5" stroke="#525252" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Transférer</span>
          </button>
          <button
            onClick={() => { setReplyingTo(message.id); setShowDropdown(false); setHovered(false); }}
            className="flex items-center hover:bg-neutral-50 w-full"
            style={{ padding: "9px 14px", gap: 10 }}
          >
            <Icon1 name="response" size={14} color="#525252" />
            <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Répondre</span>
          </button>

          {selectedDiscussionId && (
            <button
              onClick={() => {
                (isPinned ? unpinMessage : pinMessage)(selectedDiscussionId, message.id);
                setShowDropdown(false);
                setHovered(false);
              }}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <span style={{ display: "flex", width: 14, justifyContent: "center" }}>
                <PinGlyph size={14} />
              </span>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>
                {isPinned ? "Détacher" : "Épingler"}
              </span>
            </button>
          )}

          {isOwn && readStatus !== undefined && onReadInfoClick && (
            <button
              onClick={() => { onReadInfoClick(message.id); setShowDropdown(false); setHovered(false); }}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <Icon1 name="eye" size={14} color="#525252" />
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Infos du message</span>
            </button>
          )}

          {canEdit && (
            <button
              onClick={startEditing}
              className="flex items-center hover:bg-neutral-50 w-full"
              style={{ padding: "9px 14px", gap: 10 }}
            >
              <Icon1 name="modify" size={14} color="#525252" />
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#1E1E1E" }}>Modifier</span>
            </button>
          )}

          {isOwn && (
            <>
              <div style={{ height: 1, background: "#F5F5F5" }} />
              <button
                onClick={() => { deleteMessage(message.id); setShowDropdown(false); setHovered(false); }}
                className="flex items-center hover:bg-red-50 w-full"
                style={{ padding: "9px 14px", gap: 10 }}
              >
                <Icon1 name="delete" size={14} color="#ef4444" />
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 13, color: "#ef4444" }}>Supprimer</span>
              </button>
            </>
          )}
        </div>,
        document.body,
      )}

      {/* Bubble (own) / bare content column (received) */}
      <div
        style={
          flat
            ? { width: "100%", display: "flex", flexDirection: "column" }
            : {
                background: bubbleBg,
                borderRadius: bubbleRadius,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
              }
        }
      >
        {/* Reply quote — click to jump to the original */}
        {replySource && replySender && (
          <div
            onClick={msgReplyToId ? (e) => { e.stopPropagation(); setPendingScrollToMessageId(msgReplyToId); } : undefined}
            className="flex flex-row items-stretch"
            style={{
              // A filled grey block read as a bubble inside a flat row, which is
              // exactly what the flat layout removes: use a thin rule instead.
              order: flat ? -2 : 0,
              margin: flat ? "0 0 5px" : "8px 10px 2px",
              gap: 8,
              maxWidth: 380,
              width: "fit-content",
              cursor: msgReplyToId ? "pointer" : undefined,
            }}
          >
            <span style={{ width: 2, borderRadius: 999, background: isOwn ? "rgba(255,255,255,0.4)" : T.quoteRule, flexShrink: 0 }} />
            <span className="flex flex-col" style={{ minWidth: 0, gap: 1 }}>
              <span
                className="font-semibold"
                style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 11.5, color: isOwn ? "#D4D4D8" : T.accent }}
              >
                {replySender.name}
              </span>
              <span
                style={{
                  fontFamily: "Poppins",
                  fontSize: 12,
                  lineHeight: "16px",
                  color: isOwn ? "rgba(255,255,255,0.55)" : T.meta,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {replySource.deleted
                  ? "Ce message a été supprimé"
                  : replySource.text || "📎 Pièce jointe"}
              </span>
            </span>
          </div>
        )}

        {/* Images — WhatsApp-style album (with optimistic upload overlay) */}
        {imageAtts.length > 0 && (
          msgUploadProgress !== undefined && imageAtts.length === 1 ? (
            <div
              className="relative"
              style={{ margin: message.text ? "6px 4px 0" : 0, borderRadius: 8, overflow: "hidden", width: 240, height: 280 }}
            >
              <img
                src={imageAtts[0].base64Data || imageAtts[0].url}
                alt={imageAtts[0].name || "image"}
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain", display: "block" }}
              />
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="48" height="48" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                  <circle
                    cx="24" cy="24" r="20" fill="none" stroke="#FFFFFF" strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - (msgUploadProgress || 0) / 100)}`}
                    transform="rotate(-90 24 24)"
                    style={{ transition: "stroke-dashoffset 0.3s ease" }}
                  />
                  <text x="24" y="24" textAnchor="middle" dominantBaseline="central" fill="#FFFFFF" fontSize="11" fontFamily="Poppins" fontWeight="500">
                    {msgUploadProgress}%
                  </text>
                </svg>
              </div>
            </div>
          ) : (
            <div style={{ margin: message.text ? "6px 4px 0" : 4 }}>
              <ImageAlbum
                images={imageAtts}
                hovered={hovered}
                compact={compact}
                onOpen={(a) => openLightbox(imageAtts, a)}
                onDownload={(a) => downloadAtt(a)}
              />
            </div>
          )
        )}

        {/* Videos — inline players */}
        {videoAtts.map((a, i) => (
          <div key={i} style={{ margin: message.text || imageAtts.length ? "6px 4px 0" : 4 }}>
            <VideoCard att={a} onDownload={(x) => downloadAtt(x)} />
          </div>
        ))}

        {/* Documents — PDF cards or generic file rows */}
        {docAtts.map((a, i) =>
          isPdfAtt(a) ? (
            <div key={i} style={{ margin: message.text || i > 0 || imageAtts.length ? "6px 4px 0" : 4 }}>
              <PdfCard
                att={a}
                isOwn={isOwn}
                onOpen={(x) => openLightbox([x], x)}
                onDownload={(x) => downloadAtt(x)}
              />
            </div>
          ) : (
            <div
              key={i}
              className="flex flex-row items-center cursor-pointer"
              onClick={() => downloadAtt(a)}
              style={{ padding: "8px 12px", gap: 10 }}
            >
              <Icon1 name="file" size={16} color={isOwn ? "#FFFFFF" : "#000000"} />
              <span
                className="text-h2"
                style={{ fontFamily: "Poppins", fontWeight: 500, lineHeight: "15px", color: isOwn ? "#FFFFFF" : "#000000", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {a.name || "Fichier"}
              </span>
              <div
                className="flex items-center justify-center"
                style={{ width: 24, height: 24, background: isOwn ? "rgba(255,255,255,0.5)" : "#EBF6FF", borderRadius: 5 }}
              >
                <Icon1 name="download" size={16} color="#3DA7FF" />
              </div>
            </div>
          )
        )}

        {/* Sondage */}
        {message.poll && selectedDiscussionId && (
          <div style={{ padding: flat ? "2px 0 6px" : "10px 12px 2px" }}>
            <PollCard
              poll={message.poll}
              senderId={message.sender_id || message.senderId}
              isOwn={isOwn}
              currentUserId={currentUserId}
              onVote={(optId) => votePoll(selectedDiscussionId, message.id, optId)}
              onClose={() => closePoll(selectedDiscussionId, message.id)}
            />
          </div>
        )}

        {/* Audio — one player per audio attachment */}
        {audioAtts.map((a, i) =>
          a.url ? (
            <div key={i} style={{ padding: "4px 8px" }}>
              <Suspense fallback={null}>
                <AudioPlayer src={a.url} isOwn={isOwn} isDark={false} />
              </Suspense>
            </div>
          ) : null
        )}

        {/* Image / document gallery lightbox */}
        {lightbox && (() => {
          const att = lightbox.list[lightbox.idx];
          if (!att) return null;
          const multi = lightbox.list.length > 1;
          return (
            <DocumentPreviewModal
              doc={{ name: att.name || (isImageAtt(att) ? "image.png" : isPdfAtt(att) ? "document.pdf" : "fichier") }}
              url={att.base64Data || att.url || ""}
              onClose={() => setLightbox(null)}
              onDownload={() => downloadAtt(att)}
              onPrev={multi ? () => lightboxStep(-1) : undefined}
              onNext={multi ? () => lightboxStep(1) : undefined}
              counter={multi ? `${lightbox.idx + 1} / ${lightbox.list.length}` : undefined}
            />
          );
        })()}

        {/* Text content + inline edit + timestamp */}
        <div style={{ order: flat ? -1 : 0, padding: flat ? 0 : "8px 12px 6px" }}>
          {isEditing ? (
            <>
              <textarea
                ref={editInputRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === "Escape") cancelEdit();
                }}
                className="w-full outline-none resize-none bg-transparent text-h2"
                style={{
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  lineHeight: "20px",
                  color: isOwn ? "#FFFFFF" : "#000000",
                  minHeight: 40,
                  whiteSpace: "pre-wrap",
                }}
              />
              <div className="flex justify-end items-center" style={{ gap: 8, marginTop: 6 }}>
                <button
                  onClick={cancelEdit}
                  className="text-h5 hover:underline"
                  style={{ fontFamily: "Poppins", fontWeight: 500, color: isOwn ? "#A3A3A3" : "#6B7280" }}
                >
                  Annuler
                </button>
                <button
                  onClick={saveEdit}
                  className="text-h5 font-semibold hover:underline"
                  style={{ fontFamily: "Poppins", color: isOwn ? "#FFFFFF" : "#000000" }}
                >
                  Sauvegarder
                </button>
              </div>
            </>
          ) : (
            message.text && (
              <span
                className="text-h2"
                style={{
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  lineHeight: flat ? "21px" : "20px",
                  color: isOwn ? "#FFFFFF" : T.body,
                  whiteSpace: "pre-wrap",
                }}
              >
                <RenderMessageText
                  text={message.text}
                  mentions={message.mentions}
                  folderRefs={message.folder_refs}
                  isOwn={isOwn}
                />
              </span>
            )
          )}
          {flat && !isEditing && (message.edited || isPinned) && (
            <span className="inline-flex items-center" style={{ gap: 4, marginLeft: 6, verticalAlign: "middle" }}>
              {isPinned && <PinGlyph size={11} />}
              {message.edited && (
                <span style={{ fontFamily: "Poppins", fontSize: 11, fontStyle: "italic", color: T.meta }}>modifié</span>
              )}
            </span>
          )}
          {!flat && showTimestamp && !isEditing && (
            <div className="flex justify-end items-center" style={{ marginTop: 2, gap: 4 }}>
              {message.edited && (
                <span className="text-h5" style={{ fontFamily: "Poppins", color: isOwn ? "#A3A3A3" : "#A1A1A1", fontStyle: "italic" }}>
                  modifié
                </span>
              )}
              {isPinned && <PinGlyph isOwn={isOwn} size={11} />}
              <span
                className="text-h5"
                style={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  color: isOwn ? "#A3A3A3" : "#A1A1A1",
                  marginLeft: message.edited ? 0 : 12,
                }}
              >
                {message.timestamp}
              </span>
              {isOwn && readStatus && (
                <button
                  onClick={() => onReadInfoClick?.(message.id)}
                  className="flex items-center"
                  style={{ padding: 2, borderRadius: 4 }}
                  title="Voir qui a lu"
                >
                  <ReadTicks status={readStatus} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reactions */}
      {hasReactions && (
        <div
          className="flex flex-row flex-wrap items-center"
          style={{ gap: 6, marginTop: 6, justifyContent: isOwn ? "flex-end" : "flex-start" }}
        >
          {Object.entries(groupedReactions).map(([emoji, userIds]) => {
            const isMine = userIds.includes(currentUserId);
            return (
              <ReactionPill
                key={emoji}
                emoji={emoji}
                userIds={userIds}
                isMine={isMine}
                onToggle={() => toggleReaction(message.id, emoji)}
              />
            );
          })}
        </div>
      )}

      {/* Full emoji picker portal */}
      {forwardOpen && (
        <ForwardModal
          message={message}
          currentDiscussionId={selectedDiscussionId}
          onClose={() => setForwardOpen(false)}
        />
      )}

      {fullPickerAnchor && createPortal(
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setFullPickerAnchor(null)}
          />
          <div
            style={{ position: "fixed", left: fullPickerAnchor.x, top: fullPickerAnchor.y, zIndex: 9999 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Picker
              data={emojiData}
              locale="fr"
              theme="light"
              previewPosition="none"
              skinTonePosition="none"
              onEmojiSelect={(emoji: { native: string }) => {
                toggleReaction(message.id, emoji.native);
                setFullPickerAnchor(null);
              }}
            />
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// Per-message bubble — memoized so a reaction / read-tick / edit on ONE message
// doesn't re-render every sibling bubble. Comparator covers all live data.
const MessageBubble = React.memo(MessageBubbleImpl, messageBubbleAreEqual);

// --- Message group component ---

function MessageGroupImpl({ group }: { group: MessageGroupData }) {
  const getMember = useMessagerieStore((s) => s.getMember);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const selectedDiscussionId = useMessagerieStore((s) => s.selectedDiscussionId);
  const allMessages = useMessagerieStore((s) => selectedDiscussionId ? s.messages[selectedDiscussionId] : undefined);
  const readReceipts = useMessagerieStore((s) => (selectedDiscussionId && s.readReceipts[selectedDiscussionId]) || EMPTY_READ_RECEIPTS);
  const allMemberIds = useMessagerieStore((s) => {
    if (!selectedDiscussionId) return [];
    return s.discussions.find((d) => d.id === selectedDiscussionId)?.members || [];
  });

  const [readInfoMsgId, setReadInfoMsgId] = useState<string | null>(null);

  const sender = getMember(group.senderId) || { id: group.senderId, name: group.messages[0]?.sender_name || "Inconnu", role: "", online: false };

  const count = group.messages.length;
  const msgs = allMessages || [];

  const getReadStatus = (msgId: string): "sent" | "read_some" | "read_all" | "delivered" | "read" | "failed" => {
    const msg = msgs.find((m) => m.id === msgId);
    if (!msg) return "sent";

    // WhatsApp messages: use delivery status directly
    if (msg.channel === "whatsapp" && msg.wa_status) {
      if (msg.wa_status === "queued") return "sent";
      return msg.wa_status as "sent" | "delivered" | "read" | "failed";
    }

    // Email messages: use delivery status
    if (msg.channel === "email" && msg.email_status) {
      if (msg.email_status === "queued") return "sent";
      return msg.email_status as "sent" | "delivered" | "failed";
    }

    // Internal messages: use read receipts
    const msgIndex = msgs.indexOf(msg);
    const otherMemberIds = allMemberIds.filter((id) => id !== currentUserId);
    if (otherMemberIds.length === 0) return "sent";
    const otherReceipts = readReceipts.filter((r) => r.user_id !== currentUserId);
    let readCount = 0;
    for (const receipt of otherReceipts) {
      const receiptIndex = msgs.findIndex((m) => m.id === receipt.last_read_message_id);
      if (receiptIndex >= msgIndex) readCount++;
    }
    if (readCount === 0) return "sent";
    if (readCount >= otherMemberIds.length) return "read_all";
    return "read_some";
  };

  const readInfoMsgIndex = readInfoMsgId ? msgs.findIndex((m) => m.id === readInfoMsgId) : -1;

  // A message carrying nothing but images: several in a row used to stack
  // full-height previews, so a handful of photos buried the conversation. They
  // render side by side as thumbnails, each still its own message.
  const isPhotoOnly = (m: ChatMessage) => {
    if (m.deleted || m.text || m.reply_to_preview) return false;
    const atts = getAttachments(m);
    return atts.length > 0 && atts.every(isImageAtt);
  };

  const renderGroupBody = () => {
    const out: React.ReactNode[] = [];
    const list = group.messages;
    let i = 0;
    const bubble = (msg: ChatMessage, idx: number, compact: boolean) => {
      const position: BubblePosition =
        compact || count === 1 ? "solo" : idx === 0 ? "first" : idx === count - 1 ? "last" : "middle";
      const showTimestamp = !compact && idx === count - 1;
      return (
        <MessageBubble
          key={msg.id}
          message={msg}
          position={position}
          showTimestamp={showTimestamp}
          compact={compact}
          readStatus={group.isOwn && showTimestamp ? getReadStatus(msg.id) : undefined}
          onReadInfoClick={group.isOwn ? setReadInfoMsgId : undefined}
        />
      );
    };
    while (i < list.length) {
      if (isPhotoOnly(list[i])) {
        let j = i;
        while (j < list.length && isPhotoOnly(list[j])) j += 1;
        if (j - i >= 2) {
          out.push(
            <div
              key={`strip-${list[i].id}`}
              className="flex flex-row flex-wrap"
              style={{ gap: 4, justifyContent: group.isOwn ? "flex-end" : "flex-start" }}
            >
              {list.slice(i, j).map((m) => bubble(m, -1, true))}
            </div>,
          );
          i = j;
          continue;
        }
      }
      out.push(bubble(list[i], i, false));
      i += 1;
    }
    return out;
  };

  return (
    <>
      <div
        className="flex flex-row"
        style={{
          justifyContent: group.isOwn ? "flex-end" : "flex-start",
          gap: 8,
        }}
      >
        {/* Received: flat row — rounded-square avatar, "Nom · heure" header,
            bare text. Own: unchanged black bubble column, right-aligned. */}
        {!group.isOwn && (
          <div className="flex-shrink-0" style={{ width: T.avatar, paddingTop: 2 }}>
            <MemberAvatar member={sender} size={T.avatar} radius={T.avatarRadius} />
          </div>
        )}

        <div
          className="flex flex-col"
          style={{
            ...(group.isOwn
              ? { alignItems: "flex-end", gap: 3, maxWidth: `min(65%, ${T.maxMsg}px, calc(100% - ${T.toolbarGutter}px))` }
              : { alignItems: "flex-start", gap: 4, flex: 1, minWidth: 0 }),
          }}
        >
          {!group.isOwn && (
            <div className="flex flex-row items-baseline" style={{ gap: 7 }}>
              <span className="font-semibold" style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14, color: T.name }}>
                {sender.name}
              </span>
              <span style={{ color: T.meta, fontSize: 12 }}>·</span>
              <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 12, color: T.meta }}>
                {group.messages[0]?.timestamp}
              </span>
            </div>
          )}

          {renderGroupBody()}
        </div>
      </div>

      {readInfoMsgId && readInfoMsgIndex !== -1 && (
        <ReadInfoPanel
          msgIndex={readInfoMsgIndex}
          messages={msgs}
          readReceipts={readReceipts}
          allMemberIds={allMemberIds}
          currentUserId={currentUserId}
          onClose={() => setReadInfoMsgId(null)}
        />
      )}
    </>
  );
}

/** Compare a message group: sender / ownership + every message deeply. */
function messageGroupAreEqual(
  prev: { group: MessageGroupData },
  next: { group: MessageGroupData },
): boolean {
  const a = prev.group;
  const b = next.group;
  if (a === b) return true;
  if (a.senderId !== b.senderId || a.isOwn !== b.isOwn) return false;
  if (a.messages.length !== b.messages.length) return false;
  for (let i = 0; i < a.messages.length; i++) {
    if (!chatMessageEqual(a.messages[i], b.messages[i])) return false;
  }
  return true;
}

// Memoized group: read-status / receipts come through store hooks inside the
// component, so they stay live even when the `group` prop is unchanged.
const MessageGroup = React.memo(MessageGroupImpl, messageGroupAreEqual);

// --- Attachment menu ---

function AttachmentMenu({
  onImageSelect,
  onFileSelect,
  onAudioSelect,
  onPollSelect,
  onClose,
}: {
  onImageSelect: () => void;
  onFileSelect: () => void;
  onAudioSelect: () => void;
  onPollSelect?: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute z-50 flex flex-col"
      style={{
        bottom: "100%",
        left: 0,
        marginBottom: 8,
        background: "#FFFFFF",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: 10,
        overflow: "hidden",
        minWidth: 160,
      }}
    >
      <button
        onClick={onImageSelect}
        className="flex flex-row items-center hover:bg-neutral-50 text-h2"
        style={{
          padding: "10px 14px",
          gap: 10,
          fontFamily: "Poppins",
          fontWeight: 500,
          color: "#000000",
        }}
      >
        <Icon1 name="image" size={16} color="#A1A1A1" />
        Image
      </button>
      {onPollSelect && (
        <button
          onClick={onPollSelect}
          className="flex flex-row items-center hover:bg-neutral-50 text-h2"
          style={{ padding: "10px 14px", gap: 10, fontFamily: "Poppins", fontWeight: 500, color: "#000000" }}
        >
          <Icon1 name="poll" size={16} color="#A1A1A1" />
          Sondage
        </button>
      )}
      <button
        onClick={onFileSelect}
        className="flex flex-row items-center hover:bg-neutral-50 text-h2"
        style={{
          padding: "10px 14px",
          gap: 10,
          fontFamily: "Poppins",
          fontWeight: 500,
          color: "#000000",
        }}
      >
        <Icon1 name="join" size={16} color="#A1A1A1" />
        Fichier
      </button>
      <button
        onClick={onAudioSelect}
        className="flex flex-row items-center hover:bg-neutral-50 text-h2"
        style={{
          padding: "10px 14px",
          gap: 10,
          fontFamily: "Poppins",
          fontWeight: 500,
          color: "#000000",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A1A1A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="1" width="6" height="12" rx="3" />
          <path d="M5 10a7 7 0 0 0 14 0" />
          <line x1="12" y1="17" x2="12" y2="21" />
          <line x1="8" y1="21" x2="16" y2="21" />
        </svg>
        Audio
      </button>
    </div>
  );
}

// --- Chat input ---

function ChatInput() {
  const sendMessage = useMessagerieStore((s) => s.sendMessage);
  const sendTyping = useMessagerieStore((s) => s.sendTyping);
  const clearTyping = useMessagerieStore((s) => s.clearTyping);
  const selectedDiscussionId = useMessagerieStore((s) => s.selectedDiscussionId);
  const replyingToMessageId = useMessagerieStore((s) => s.replyingToMessageId);
  const setReplyingTo = useMessagerieStore((s) => s.setReplyingTo);
  const getMessageById = useMessagerieStore((s) => s.getMessageById);
  const getMember = useMessagerieStore((s) => s.getMember);
  const getMatchingMembers = useMessagerieStore((s) => s.getMatchingMembers);

  const [inputText, setInputText] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStartIdx, setMentionStartIdx] = useState<number>(0);
  const [trackedMentions, setTrackedMentions] = useState<string[]>([]);
  const [folderQuery, setFolderQuery] = useState<string | null>(null);
  const [folderStartIdx, setFolderStartIdx] = useState<number>(0);
  const [folderIndex, setFolderIndex] = useState(0);
  const [trackedFolders, setTrackedFolders] = useState<FolderRef[]>([]);
  const { results: folderResults, loading: folderLoading } = useFolderSearch(folderQuery);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSendRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const replyMessage = replyingToMessageId
    ? getMessageById(replyingToMessageId)
    : undefined;
  const replySender = replyMessage
    ? getMember(replyMessage.sender_id || replyMessage.senderId || "")
    : undefined;

  const matchingMembers =
    mentionQuery !== null ? getMatchingMembers(mentionQuery) : [];

  useEffect(() => {
    if (replyingToMessageId) {
      inputRef.current?.focus();
    }
  }, [replyingToMessageId]);

  // Clear typing on unmount or discussion change
  useEffect(() => {
    const discId = selectedDiscussionId;
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (discId) clearTyping(discId);
    };
  }, [selectedDiscussionId, clearTyping]);

  const [composerPicker, setComposerPicker] = useState<{ x: number; y: number } | null>(null);
  const [showPoll, setShowPoll] = useState(false);
  const createPoll = useMessagerieStore((s) => s.createPoll);

  // Insert at the caret rather than appending, so an emoji lands where the
  // user is typing.
  const insertEmojiAtCaret = (emoji: string) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? inputText.length;
    const end = el?.selectionEnd ?? start;
    const next = inputText.slice(0, start) + emoji + inputText.slice(end);
    setInputText(next);
    requestAnimationFrame(() => {
      el?.focus();
      const caret = start + emoji.length;
      el?.setSelectionRange(caret, caret);
    });
  };

  const handleSend = () => {
    if (!inputText.trim()) return;
    const usedFolders = trackedFolders.filter((f) => inputText.includes(`/${f.name}`));
    const opts = usedFolders.length > 0
      ? { folderRefs: usedFolders.map((f) => ({ id: f.id, name: f.name })) }
      : undefined;
    sendMessage(
      inputText,
      undefined,
      trackedMentions.length > 0 ? trackedMentions : undefined,
      undefined,
      opts,
    );
    setInputText("");
    setTrackedMentions([]);
    setTrackedFolders([]);
    setFolderQuery(null);
    setMentionQuery(null);
    // Clear typing indicator immediately on send
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (selectedDiscussionId) clearTyping(selectedDiscussionId);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (folderQuery !== null && folderResults.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setFolderIndex((i) => Math.min(i + 1, folderResults.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setFolderIndex((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); handleFolderSelect(folderResults[folderIndex]); return; }
      if (e.key === "Escape") { e.preventDefault(); setFolderQuery(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (mentionQuery !== null && matchingMembers.length > 0) {
        handleMentionSelect(matchingMembers[0]);
      } else {
        handleSend();
      }
    }
    if (e.key === "Escape" && mentionQuery !== null) {
      setMentionQuery(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputText(value);
    if (selectedDiscussionId && value.trim()) {
      const now = Date.now();
      if (now - lastSendRef.current > 2000) {
        sendTyping(selectedDiscussionId);
        lastSendRef.current = now;
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        clearTyping(selectedDiscussionId);
      }, 3000);
    } else if (selectedDiscussionId && !value.trim()) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      clearTyping(selectedDiscussionId);
    }

    // Auto-resize
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";

    // Detect @ mentions
    const cursorPos = e.target.selectionStart || value.length;
    const textBefore = value.substring(0, cursorPos);
    const atIdx = textBefore.lastIndexOf("@");

    if (atIdx >= 0) {
      const charBefore = atIdx > 0 ? textBefore[atIdx - 1] : " ";
      if (charBefore === " " || charBefore === "\n" || atIdx === 0) {
        const query = textBefore.substring(atIdx + 1);
        if (!query.includes(" ")) {
          setMentionQuery(query);
          setMentionStartIdx(atIdx);
          setFolderQuery(null);
          return;
        }
      }
    }
    setMentionQuery(null);

    // Detect "/" folder trigger (only after whitespace / at start).
    const slashIdx = textBefore.lastIndexOf("/");
    if (slashIdx >= 0) {
      const charBefore = slashIdx > 0 ? textBefore[slashIdx - 1] : " ";
      if (charBefore === " " || charBefore === "\n" || slashIdx === 0) {
        const query = textBefore.substring(slashIdx + 1);
        if (!query.includes(" ") && !query.includes("/")) {
          setFolderQuery(query);
          setFolderStartIdx(slashIdx);
          setFolderIndex(0);
          return;
        }
      }
    }
    setFolderQuery(null);
  };

  const handleFolderSelect = (ref: FolderRef) => {
    const before = inputText.substring(0, folderStartIdx);
    const after = inputText.substring(folderStartIdx + 1 + (folderQuery?.length || 0));
    const newText = `${before}/${ref.name} ${after}`;
    setInputText(newText);
    setFolderQuery(null);
    setTrackedFolders((prev) => prev.some((f) => f.id === ref.id) ? prev : [...prev, ref]);
    inputRef.current?.focus();
  };

  const handleMentionSelect = (member: ChatMember) => {
    const before = inputText.substring(0, mentionStartIdx);
    const after = inputText.substring(
      mentionStartIdx + 1 + (mentionQuery?.length || 0),
    );
    const newText = `${before}@${member.name} ${after}`;
    setInputText(newText);
    setMentionQuery(null);
    setTrackedMentions((prev) =>
      prev.includes(member.id) ? prev : [...prev, member.id],
    );
    inputRef.current?.focus();
  };

  const handleImageSelect = () => {
    setShowAttachMenu(false);
    imageInputRef.current?.click();
  };

  const handleFileSelect = () => {
    setShowAttachMenu(false);
    fileInputRef.current?.click();
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    sendMessage("", undefined, undefined, file);
    e.target.value = "";
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    sendMessage("", undefined, undefined, file);
    e.target.value = "";
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus") ? "audio/ogg;codecs=opus"
        : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordingChunksRef.current.push(e.data); };
      recorder.start(250);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch { /* microphone access denied */ }
  }, []);

  const stopRecording = useCallback((send: boolean) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    recorder.onstop = () => {
      recorder.stream.getTracks().forEach((t) => t.stop());
      if (send && recordingChunksRef.current.length > 0) {
        const ext = recorder.mimeType.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(recordingChunksRef.current, { type: recorder.mimeType });
        const file = new File([blob], `audio_${Date.now()}.${ext}`, { type: recorder.mimeType });
        sendMessage("", undefined, undefined, file);
      }
      recordingChunksRef.current = [];
    };
    recorder.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
    setRecordingTime(0);
  }, [sendMessage]);

  const handleAudioSelect = () => {
    setShowAttachMenu(false);
    startRecording();
  };

  return (
    <div className="flex flex-col justify-end" style={{ flexShrink: 0 }}>
      {/* Reply preview */}
      {replyMessage && replySender && (
        <div
          className="flex flex-row items-center justify-between"
          style={{
            padding: "8px 12px",
            marginBottom: 4,
            background: "#F5F5F5",
            borderRadius: "10px 10px 0 0",
            borderLeft: "3px solid #525252",
          }}
        >
          <div
            className="flex flex-col"
            style={{ gap: 2, overflow: "hidden", flex: 1 }}
          >
            <span
              className="text-h3 font-semibold"
              style={{ fontFamily: "Poppins", color: "#525252" }}
            >
              {replySender.name}
            </span>
            <span
              className="text-h4 text-neutral-400"
              style={{
                fontFamily: "Poppins",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {replyMessage.text || "📎 Pièce jointe"}
            </span>
          </div>
          <button
            onClick={() => setReplyingTo(null)}
            className="flex-shrink-0 hover:bg-neutral-200 rounded-full flex items-center justify-center"
            style={{ width: 24, height: 24 }}
          >
            <Icon1 name="close" size={10} color="#A1A1A1" />
          </button>
        </div>
      )}

      {isRecording ? (
        <div
          className="flex flex-row items-center"
          style={{
            padding: "8px 10px",
            gap: 10,
            background: "#F5F5F5",
            borderRadius: replyMessage ? "0 0 10px 10px" : 10,
          }}
        >
          <button
            onClick={() => stopRecording(false)}
            className="hover:bg-neutral-200 rounded-full flex items-center justify-center"
            style={{ width: 28, height: 28 }}
          >
            <Icon1 name="close" size={10} color="#EF4444" />
          </button>
          <div className="flex-1 flex items-center" style={{ gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, background: "#EF4444", animation: "pulse 1s infinite" }} />
            <span className="text-h2" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#525252" }}>
              {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, "0")}
            </span>
          </div>
          <button
            onClick={() => stopRecording(true)}
            className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
            style={{ width: 28, height: 28 }}
          >
            <Icon1 name="send" size={16} color="#525252" />
          </button>
        </div>
      ) : (
      <div
        className="flex flex-row items-center relative"
        style={{
          padding: "8px 10px",
          gap: 10,
          background: "#F5F5F5",
          borderRadius: replyMessage ? "0 0 10px 10px" : 10,
        }}
      >
        {/* Attachment button */}
        <div className="relative">
          <button
            onClick={() => setShowAttachMenu(!showAttachMenu)}
            className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
            style={{ width: 28, height: 28 }}
          >
            <Icon1 name="plus" size={12} color="#A1A1A1" />
          </button>
          {showAttachMenu && (
            <AttachmentMenu
              onImageSelect={handleImageSelect}
              onFileSelect={handleFileSelect}
              onAudioSelect={handleAudioSelect}
              onPollSelect={() => { setShowAttachMenu(false); setShowPoll(true); }}
              onClose={() => setShowAttachMenu(false)}
            />
          )}
        </div>

        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Entrez un message"
            rows={1}
            className="w-full outline-none border-none text-h2 resize-none"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              color: "#000000",
              background: "transparent",
              maxHeight: 120,
              overflowY: "auto",
            }}
          />

          {/* Folder ("/") dropdown */}
          {folderQuery !== null && (
            <div className="absolute z-50" style={{ bottom: "100%", left: 0, right: 0, marginBottom: 12 }}>
              <FolderSearchPopover
                results={folderResults}
                loading={folderLoading}
                activeIndex={folderIndex}
                onPick={handleFolderSelect}
                onHover={setFolderIndex}
              />
            </div>
          )}

          {/* Mention dropdown */}
          {mentionQuery !== null && matchingMembers.length > 0 && (
            <div
              className="absolute z-50 flex flex-col"
              style={{
                bottom: "100%",
                left: 0,
                marginBottom: 12,
                background: "#FFFFFF",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                borderRadius: 10,
                overflow: "hidden",
                minWidth: 200,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {matchingMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => handleMentionSelect(member)}
                  className="flex flex-row items-center hover:bg-neutral-50"
                  style={{
                    padding: "8px 12px",
                    gap: 10,
                  }}
                >
                  <MemberAvatar member={member} size={24} />
                  <div className="flex flex-col items-start">
                    <span
                      className="text-h2"
                      style={{
                        fontFamily: "Poppins",
                        fontWeight: 500,
                        color: "#000000",
                      }}
                    >
                      {member.name}
                    </span>
                    <span
                      className="text-h4 text-neutral-400"
                      style={{ fontFamily: "Poppins" }}
                    >
                      {member.role}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Emoji picker for the composer — same emoji-mart picker as the one
            behind the "+" of the reaction row, so the app has one picker. */}
        <div className="relative">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
              setComposerPicker({
                x: Math.min(r.left, window.innerWidth - 360),
                y: Math.max(8, r.top - 440),
              });
            }}
            title="Emoji"
            className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
            style={{ width: 28, height: 28 }}
          >
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6.2" stroke="#A1A1A1" strokeWidth="1.3" />
              <circle cx="5.9" cy="6.6" r="0.7" fill="#A1A1A1" />
              <circle cx="10.1" cy="6.6" r="0.7" fill="#A1A1A1" />
              <path d="M5.4 9.7c.7.9 1.6 1.4 2.6 1.4s1.9-.5 2.6-1.4" stroke="#A1A1A1" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
          style={{ width: 28, height: 28 }}
        >
          <Icon1
            name="send"
            size={16}
            color={inputText.trim() ? "#525252" : "#A1A1A1"}
          />
        </button>
      </div>
      )}

      {showPoll && selectedDiscussionId && (
        <PollComposerModal
          onClose={() => setShowPoll(false)}
          onCreate={(poll) => createPoll(selectedDiscussionId, poll)}
        />
      )}

      {composerPicker && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setComposerPicker(null)} />
          <div
            style={{ position: "fixed", left: composerPicker.x, top: composerPicker.y, zIndex: 9999 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Picker
              data={emojiData}
              locale="fr"
              theme="light"
              previewPosition="none"
              skinTonePosition="none"
              onEmojiSelect={(emoji: { native: string }) => {
                insertEmojiAtCaret(emoji.native);
                setComposerPicker(null);
              }}
            />
          </div>
        </>,
        document.body
      )}

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageFile}
        style={{ display: "none" }}
      />
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileAttach}
        style={{ display: "none" }}
      />
    </div>
  );
}

// --- Chat panel (right) ---

// --- Chat header actions dropdown ---

function ChatActionsDropdown({
  onInfo,
  onAddMembers,
  onMuteNotifs,
  onLeave,
  isMuted,
}: {
  onInfo: () => void;
  onAddMembers: () => void;
  onMuteNotifs: () => void;
  onLeave: () => void;
  isMuted: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="hover:bg-neutral-200 rounded-md flex items-center justify-center"
        style={{ width: 32, height: 32 }}
      >
        <Icon1 name="down" size={16} color="#666666" />
      </button>
      {open && (
        <div
          className={`
            ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-90"}
            absolute top-full right-0 z-50 w-300 transition-all duration-200 flex flex-col text-shadow_50 border-1 border-shadow_10 bg-white shadow-xl rounded-lg
          `}
          style={{ marginTop: 4 }}
        >
          <div className="flex flex-col p-1">
            <SideMenuButton
              size="large"
              label="Infos de la conversation"
              name="question"
              onClick={() => { onInfo(); setOpen(false); }}
            />
            <SideMenuButton
              size="large"
              label="Ajouter des membres"
              name="user"
              onClick={() => { onAddMembers(); setOpen(false); }}
            />
            <SideMenuButton
              size="large"
              label={isMuted ? "Réactiver les notifications" : "Couper les notifications"}
              name="cloche"
              onClick={() => { onMuteNotifs(); setOpen(false); }}
            />
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <SideMenuButton
              size="large"
              label="Quitter la conversation"
              name="logout"
              color="#ef4444"
              onClick={() => { onLeave(); setOpen(false); }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// --- Add members modal ---

function AddMembersModal({
  onClose,
  discussionId,
}: {
  onClose: () => void;
  discussionId: string;
}) {
  const members = useMessagerieStore((s) => s.members);
  const discussions = useMessagerieStore((s) => s.discussions);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const fetchDiscussions = useMessagerieStore((s) => s.fetchDiscussions);
  const activeTab = useMessagerieStore((s) => s.activeTab);

  const discussion = discussions.find((d) => d.id === discussionId);
  const existingMembers = discussion?.members || [];

  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const available = members.filter(
    (m) => m.id !== currentUserId && !existingMembers.includes(m.id),
  );
  const filtered = search
    ? available.filter((m) => includesNormalized(m.name, search))
    : available;

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAdd = async () => {
    if (selected.length === 0 || !discussion || saving) return;
    setSaving(true);
    setError(null);
    try {
      await messagerieApi.addMembers(discussionId, selected);
      await fetchDiscussions(activeTab);
      onClose();
    } catch (e) {
      console.error("Failed to add members", e);
      setError("Impossible d'ajouter les membres. Veuillez réessayer.");
      setSaving(false);
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      title="Ajouter des membres"
      startIcon={<Icon1 name="user" size={20} />}
      width="max-w-sm"
    >
      <div className="flex flex-col gap-3 p-4">
        <SearchField
          value={search}
          onChange={(v) => setSearch(v)}
          placeholder="Rechercher"
        />
        <div
          className="flex flex-col"
          style={{ maxHeight: 240, overflowY: "auto", gap: 2 }}
        >
          {filtered.length === 0 && (
            <span className="text-h3 text-neutral-400 py-3 text-center">
              Aucun membre disponible
            </span>
          )}
          {filtered.map((m) => {
            const sel = selected.includes(m.id);
            return (
              <button
                key={m.id}
                onClick={() => toggle(m.id)}
                className={`flex flex-row items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 ${
                  sel ? "bg-indigo-50" : ""
                }`}
              >
                <MemberAvatar member={m} size={28} />
                <span className="text-h3 font-medium text-black flex-1 text-left">
                  {m.name}
                </span>
                {sel && <Icon1 name="check" size={14} color="#6366f1" />}
              </button>
            );
          })}
        </div>
        {error && (
          <span className="text-h4 text-red-600 text-center">{error}</span>
        )}
        <Button
          label={saving ? "Ajout…" : `Ajouter (${selected.length})`}
          onClick={handleAdd}
          disabled={selected.length === 0 || saving}
          variant="large"
        />
      </div>
    </BaseModal>
  );
}

// --- Conversation info modal ---

// --- Conv info side panel ---

type InfoTab = "medias" | "fichiers" | "liens";

function ConvInfoPanel({
  onClose,
  discussion,
  chatMembers,
  messages,
}: {
  onClose: () => void;
  discussion: Discussion;
  chatMembers: ChatMember[];
  messages: ChatMessage[];
}) {
  const [activeTab, setActiveTab] = useState<InfoTab>("medias");
  const [previewImage, setPreviewImage] = useState<ChatMessage | null>(null);

  const handleDownload = async (url?: string, base64?: string, name?: string) => {
    const href = base64 || url;
    if (!href) {
      alert(`Téléchargement simulé : ${name || "fichier"}`);
      return;
    }
    try {
      const res = await fetch(href);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(href, "_blank");
    }
  };

  const medias = messages.filter(
    (m) => m.attachment?.type === "image",
  );
  const files = messages.filter(
    (m) => m.attachment?.type === "file",
  );

  // Extract URLs from message texts
  const urlRegex = /https?:\/\/[^\s]+/g;
  const links: { url: string; messageId: string }[] = [];
  messages.forEach((m) => {
    if (m.text) {
      const matches = m.text.match(urlRegex);
      if (matches) {
        matches.forEach((url) => links.push({ url, messageId: m.id }));
      }
    }
  });

  const tabs: { id: InfoTab; label: string; count: number }[] = [
    { id: "medias", label: "Médias", count: medias.length },
    { id: "fichiers", label: "Fichiers", count: files.length },
    { id: "liens", label: "Liens", count: links.length },
  ];

  return (
    <div
      className="flex flex-col flex-shrink-0"
      style={{
        width: 340,
        height: "100%",
        background: "#FFFFFF",
        borderLeft: "1px solid #EEEEEE",
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <div
        className="flex flex-row items-center justify-between flex-shrink-0"
        style={{ padding: "20px 20px 0" }}
      >
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: 16,
            color: "#000000",
          }}
        >
          Infos
        </span>
        <button
          onClick={onClose}
          className="hover:bg-neutral-100 rounded-full flex items-center justify-center"
          style={{ width: 28, height: 28 }}
        >
          <Icon1 name="close" size={12} color="#A1A1A1" />
        </button>
      </div>

      {/* Conv name & description */}
      <div className="flex flex-col flex-shrink-0" style={{ padding: "16px 20px", gap: 12 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 18,
              color: "#000000",
            }}
          >
            {discussion.name}
          </span>
          {discussion.description && (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: 13,
                color: "#888888",
              }}
            >
              {discussion.description}
            </span>
          )}
        </div>
      </div>

      {/* Members */}
      <div
        className="flex flex-col flex-shrink-0"
        style={{
          padding: "0 20px 16px",
          gap: 8,
          borderBottom: "1px solid #F0F0F0",
        }}
      >
        <span
          style={{
            fontFamily: "Poppins",
            fontWeight: 600,
            fontSize: 13,
            color: "#A1A1A1",
          }}
        >
          Membres ({chatMembers.length})
        </span>
        <div className="flex flex-col" style={{ gap: 2 }}>
          {chatMembers.map((m) => (
            <div
              key={m.id}
              className="flex flex-row items-center"
              style={{ gap: 10, padding: "5px 0" }}
            >
              <MemberAvatar member={m} size={30} />
              <div className="flex flex-col flex-1" style={{ gap: 0 }}>
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: 13,
                    color: "#000000",
                  }}
                >
                  {m.name}
                </span>
                <span
                  style={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: 11,
                    color: "#AAAAAA",
                  }}
                >
                  {m.role}
                </span>
              </div>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: m.online ? "#5EEAD4" : "#D4D4D4",
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Tabs: Medias / Fichiers / Liens */}
      <div
        className="flex flex-row flex-shrink-0"
        style={{ padding: "12px 20px 0", gap: 0 }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className="flex-1 flex items-center justify-center"
            style={{
              fontFamily: "Poppins",
              fontWeight: activeTab === t.id ? 600 : 500,
              fontSize: 13,
              color: activeTab === t.id ? "#000000" : "#AAAAAA",
              padding: "8px 0",
              background: "transparent",
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              borderBottomWidth: 2,
              borderBottomStyle: "solid",
              borderBottomColor: activeTab === t.id ? "#6366f1" : "transparent",
            }}
          >
            {t.label} {t.count > 0 && <span style={{ marginLeft: 4, color: "#AAAAAA", fontWeight: 400 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1" style={{ padding: "12px 20px", overflowY: "auto", minHeight: 0 }}>
        {activeTab === "medias" && (
          medias.length === 0 ? (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 13,
                color: "#CCCCCC",
                display: "block",
                textAlign: "center",
                marginTop: 30,
              }}
            >
              Aucun média
            </span>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 4,
              }}
            >
              {medias.map((m) => (
                <div
                  key={m.id}
                  className="group relative cursor-pointer"
                  style={{
                    aspectRatio: "1",
                    borderRadius: 6,
                    overflow: "hidden",
                    background: "#F5F5F5",
                  }}
                  onClick={() => setPreviewImage(m)}
                >
                  <img
                    src={m.attachment!.base64Data || m.attachment!.url}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "rgba(0,0,0,0.35)" }}
                  >
                    <Icon1 name="eye" size={20} color="#FFFFFF" />
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === "fichiers" && (
          files.length === 0 ? (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 13,
                color: "#CCCCCC",
                display: "block",
                textAlign: "center",
                marginTop: 30,
              }}
            >
              Aucun fichier
            </span>
          ) : (
            <div className="flex flex-col" style={{ gap: 4 }}>
              {files.map((m) => (
                <div
                  key={m.id}
                  className="flex flex-row items-center hover:bg-neutral-50 cursor-pointer"
                  style={{
                    padding: "8px 10px",
                    gap: 10,
                    borderRadius: 8,
                  }}
                  onClick={() =>
                    handleDownload(
                      m.attachment!.url,
                      m.attachment!.base64Data,
                      m.attachment!.name,
                    )
                  }
                >
                  <Icon1 name="file" size={18} color="#6366f1" />
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 13,
                      color: "#000000",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.attachment!.name || "Fichier"}
                  </span>
                  <Icon1 name="download" size={14} color="#BBBBBB" />
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 400,
                      fontSize: 11,
                      color: "#BBBBBB",
                      flexShrink: 0,
                    }}
                  >
                    {m.timestamp}
                  </span>
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === "liens" && (
          links.length === 0 ? (
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: 13,
                color: "#CCCCCC",
                display: "block",
                textAlign: "center",
                marginTop: 30,
              }}
            >
              Aucun lien
            </span>
          ) : (
            <div className="flex flex-col" style={{ gap: 4 }}>
              {links.map((l, i) => (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-row items-center hover:bg-neutral-50"
                  style={{
                    padding: "8px 10px",
                    gap: 10,
                    borderRadius: 8,
                    textDecoration: "none",
                  }}
                >
                  <Icon1 name="linkedFile" size={18} color="#6366f1" />
                  <span
                    style={{
                      fontFamily: "Poppins",
                      fontWeight: 500,
                      fontSize: 13,
                      color: "#6366f1",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {l.url}
                  </span>
                </a>
              ))}
            </div>
          )
        )}
      </div>

      {previewImage && previewImage.attachment && (
        <DocumentPreviewModal
          doc={{ name: previewImage.attachment.name || "image.png" }}
          url={previewImage.attachment.base64Data || previewImage.attachment.url || ""}
          onClose={() => setPreviewImage(null)}
          onDownload={() =>
            handleDownload(
              previewImage.attachment!.url,
              previewImage.attachment!.base64Data,
              previewImage.attachment!.name || "image.png",
            )
          }
        />
      )}
    </div>
  );
}

function FlatChatPanel({ onToggleInfo }: { onToggleInfo: () => void }) {
  const selectedDiscussionId = useMessagerieStore(
    (s) => s.selectedDiscussionId,
  );
  const allMessages = useMessagerieStore((s) => s.messages);
  const discussions = useMessagerieStore((s) => s.discussions);
  const members = useMessagerieStore((s) => s.members);
  const isLoadingMessages = useMessagerieStore((s) => s.isLoadingMessages);
  const isLoadingOlderMessages = useMessagerieStore((s) => s.isLoadingOlderMessages);
  const hasMoreMessages = useMessagerieStore((s) => s.hasMoreMessages);
  const fetchOlderMessages = useMessagerieStore((s) => s.fetchOlderMessages);
  const typingUsers = useMessagerieStore((s) => s.typingUsers);
  const getMember = useMessagerieStore((s) => s.getMember);
  const currentUserId = useMessagerieStore((s) => s.currentUserId);
  const unpinMessage = useMessagerieStore((s) => s.unpinMessage);
  const pendingScrollToMessageId = useMessagerieStore((s) => s.pendingScrollToMessageId);
  const setPendingScrollToMessageId = useMessagerieStore((s) => s.setPendingScrollToMessageId);
  const clearPendingScrollToMessageId = useMessagerieStore((s) => s.clearPendingScrollToMessageId);
  const ingestLiveMessages = useMessagerieStore((s) => s.ingestLiveMessages);
  const fetchReadReceipts = useMessagerieStore((s) => s.fetchReadReceipts);

  // Live thread: the WebSocket already pushes new_message, but it is a single
  // in-memory hop — a dropped socket or a second backend instance silently
  // stops it. Firestore is the same source of truth the backend writes to, and
  // the SDK owns reconnection, so the two together make delivery reliable.
  // Snapshot docs are merged incrementally (no HTTP round-trip per change).
  useEffect(() => {
    if (!selectedDiscussionId) return;
    const q = query(
      collection(firebase.db, "discussions", selectedDiscussionId, "messages"),
      orderBy("created_at", "desc"),
      fsLimit(50),
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const changes = snapshot.docChanges();
      if (changes.length === 0) return;
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      const removed = changes.filter((c) => c.type === "removed").map((c) => c.doc.id);
      ingestLiveMessages(selectedDiscussionId, docs, removed);
    }, (err) => {
      console.warn("[Messagerie] messages listener error:", err);
    });
    return () => unsub();
  }, [selectedDiscussionId, ingestLiveMessages]);

  // Read receipts live in their own sub-collection.
  useEffect(() => {
    if (!selectedDiscussionId) return;
    const q = query(collection(firebase.db, "discussions", selectedDiscussionId, "read_receipts"));
    const unsub = onSnapshot(q, (snapshot) => {
      if (snapshot.docChanges().length === 0) return;
      fetchReadReceipts(selectedDiscussionId);
    }, (err) => {
      console.warn("[Messagerie] read-receipts listener error:", err);
    });
    return () => unsub();
  }, [selectedDiscussionId, fetchReadReceipts]);

  const discussion = discussions.find((d) => d.id === selectedDiscussionId);
  const pinnedMessages = discussion?.pinned_messages || [];
  const messages = selectedDiscussionId
    ? allMessages[selectedDiscussionId] || []
    : [];
  const chatMembers = discussion
    ? members.filter((m) => discussion.members.includes(m.id))
    : [];
  const onlineCount = chatMembers.filter((m) => m.online).length;

  // Firestore real-time listener for typing indicator
  const [typingNames, setTypingNames] = useState<string[]>([]);
  useEffect(() => {
    if (!selectedDiscussionId) { setTypingNames([]); return; }
    const typingCol = collection(firebase.db, "discussions", selectedDiscussionId, "typing");
    const unsub = onSnapshot(typingCol, (snapshot) => {
      const now = Date.now();
      const names: string[] = [];
      snapshot.docs.forEach((d) => {
        const ts = d.data().timestamp;
        if (d.id === currentUserId) return; // skip self
        if (ts?.toMillis && now - ts.toMillis() > 10000) return; // stale
        const member = getMember(d.id);
        if (member) names.push(member.name.split(" ")[0]);
      });
      setTypingNames(names);
    }, (err) => {
      console.error("[Messagerie:typing] onSnapshot error:", err);
    });
    return () => { unsub(); setTypingNames([]); };
  }, [selectedDiscussionId, currentUserId, getMember]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [convSearch, setConvSearch] = useState("");
  const [showConvSearch, setShowConvSearch] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const lastSeenIdRef = useRef<string | null>(null);

  // Auto-scroll to the bottom when a genuinely new message arrives. Keyed on the
  // id of the last message, NOT on the count: the live window is capped at 50,
  // so in a busy thread a new message rotates the oldest out and the length
  // never changes — a count-based check would silently stop scrolling. Loading
  // older messages prepends, so the last id is unchanged and we stay put.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last) return;
    if (lastSeenIdRef.current === last.id) return;
    const primed = lastSeenIdRef.current !== null;
    lastSeenIdRef.current = last.id;
    messagesEndRef.current?.scrollIntoView({
      behavior: (primed ? "smooth" : "auto") as ScrollBehavior,
    });
  }, [messages, selectedDiscussionId]);

  // Reset when switching discussions so the first render jumps to the bottom.
  useEffect(() => {
    lastSeenIdRef.current = null;
  }, [selectedDiscussionId]);

  // Track which discussion is actually being viewed (for WS auto-read logic)
  const setViewingDiscussion = useMessagerieStore((s) => s.setViewingDiscussion);
  const fetchMessages = useMessagerieStore((s) => s.fetchMessages);
  useEffect(() => {
    if (selectedDiscussionId) {
      setViewingDiscussion(selectedDiscussionId);
    }
    return () => { setViewingDiscussion(null); };
  }, [selectedDiscussionId, setViewingDiscussion]);

  // NOTE: live delivery is handled by the ingestLiveMessages listener declared
  // at the top of this component. A second listener used to sit here and call
  // fetchMessages() on every snapshot, which REPLACED the whole array with the
  // last 50 messages from the server — discarding any older page the user had
  // scrolled back to, and churning the list on every keystroke-sized event.
  // That replacement is what made the thread jump on send.

  // Scroll-up detection for loading older messages
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el || !selectedDiscussionId) return;
    if (el.scrollTop < 100 && hasMoreMessages[selectedDiscussionId] && !isLoadingOlderMessages) {
      const prevScrollHeight = el.scrollHeight;
      fetchOlderMessages(selectedDiscussionId).then(() => {
        // Preserve scroll position after prepending older messages
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight - prevScrollHeight;
          }
        });
      });
    }
  };

  useEffect(() => {
    setShowConvSearch(false);
    setConvSearch("");
  }, [selectedDiscussionId]);

  // Jump to a message: scroll into view + 2s highlight flash. Pages back through
  // older messages (up to ~100 iters) if the target isn't loaded yet.
  const isJumpingRef = useRef(false);
  const jumpToMessage = useCallback(async (msgId: string) => {
    if (!selectedDiscussionId || isJumpingRef.current) return;
    isJumpingRef.current = true;
    // Clear any active conversation filter so the target isn't hidden.
    setConvSearch("");
    const flash = () => {
      const el = document.getElementById(`chat-msg-${msgId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.outline = "2px solid #6366f1";
        el.style.outlineOffset = "2px";
        el.style.background = "rgba(99,102,241,0.08)";
        setTimeout(() => {
          el.style.outline = "";
          el.style.outlineOffset = "";
          el.style.background = "";
        }, 2000);
      }
    };
    if (document.getElementById(`chat-msg-${msgId}`)) {
      flash();
      isJumpingRef.current = false;
      return;
    }
    const store = useMessagerieStore.getState;
    let attempts = 0;
    while (attempts < 100) {
      const { hasMoreMessages: hm, isLoadingOlderMessages: loading } = store();
      if (!hm[selectedDiscussionId]) break;
      if (loading) { await new Promise((r) => setTimeout(r, 100)); continue; }
      await store().fetchOlderMessages(selectedDiscussionId);
      attempts++;
      if ((store().messages[selectedDiscussionId] || []).some((m) => m.id === msgId)) break;
    }
    requestAnimationFrame(() => { flash(); isJumpingRef.current = false; });
  }, [selectedDiscussionId]);

  // Honor jump requests routed through the store (reply quotes, pinned bar).
  useEffect(() => {
    if (pendingScrollToMessageId) {
      jumpToMessage(pendingScrollToMessageId);
      clearPendingScrollToMessageId();
    }
  }, [pendingScrollToMessageId, jumpToMessage, clearPendingScrollToMessageId]);

  const handleUnpin = useCallback((msgId: string) => {
    if (selectedDiscussionId) unpinMessage(selectedDiscussionId, msgId);
  }, [selectedDiscussionId, unpinMessage]);

  // Stable jump handler for the PinnedBar so its memo isn't defeated.
  const handleJumpPinned = useCallback((id: string) => {
    setPendingScrollToMessageId(id);
  }, [setPendingScrollToMessageId]);

  // Memoize the derived arrays so the grouped list (and each MessageGroup's
  // `group` prop) stays referentially stable until the source actually changes.
  const filteredMessages = useMemo(
    () =>
      convSearch
        ? messages.filter(
            (m) => m.text && m.text.toLowerCase().includes(convSearch.toLowerCase()),
          )
        : messages,
    [messages, convSearch],
  );

  const messageGroups = useMemo(
    () => groupMessages(filteredMessages, currentUserId),
    [filteredMessages, currentUserId],
  );

  if (!discussion) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={{ background: "#FAFAFA" }}
      >
        <span
          className="text-h1"
          style={{
            fontFamily: "Poppins",
            fontWeight: 500,
            color: "#A1A1A1",
          }}
        >
          Sélectionnez une discussion
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col flex-1"
      style={{
        padding: "30px 20px",
        gap: 15,
        background: "#FAFAFA",
        minWidth: 0,
        height: "100%",
      }}
    >
      {/* Header */}
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="flex flex-row items-center justify-between">
          <span
            style={{
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "30px",
              color: "#000000",
            }}
          >
            {discussion.name}
          </span>
          <div className="flex flex-row items-center" style={{ gap: 4 }}>
            <button
              onClick={() => {
                setShowConvSearch(!showConvSearch);
                if (showConvSearch) setConvSearch("");
              }}
              className="hover:bg-neutral-200 rounded-md flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                background: showConvSearch ? "#F0F0F0" : "transparent",
              }}
            >
              <Icon1 name="search" size={16} color="#666666" />
            </button>
            <ChatActionsDropdown
              onInfo={onToggleInfo}
              onAddMembers={() => setShowAddMembers(true)}
              onMuteNotifs={() => setIsMuted(!isMuted)}
              onLeave={() => {}}
              isMuted={isMuted}
            />
          </div>
        </div>
        {showConvSearch && (
          <input
            autoFocus
            value={convSearch}
            onChange={(e) => setConvSearch(e.target.value)}
            placeholder="Rechercher dans la conversation..."
            className="outline-none border-none text-h3"
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              color: "#000000",
              background: "#F0F0F0",
              borderRadius: 8,
              padding: "8px 12px",
              marginTop: 4,
            }}
          />
        )}
        <button
          type="button"
          onClick={onToggleInfo}
          className="flex flex-row items-center cursor-pointer hover:opacity-80 transition-opacity"
          style={{ gap: 20, marginTop: 4, background: "none", border: "none", padding: 0 }}
        >
          <AvatarStack members={chatMembers} size={24} />
          <div className="flex flex-row items-center" style={{ gap: 5 }}>
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "#5EEAD4",
              }}
            />
            <span
              className="text-h2"
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                lineHeight: "18px",
                color: "#5EEAD4",
              }}
            >
              {onlineCount} en ligne
            </span>
          </div>
        </button>
      </div>

      {/* Pinned messages bar */}
      {pinnedMessages.length > 0 && (
        <PinnedBar
          pins={pinnedMessages}
          onJump={handleJumpPinned}
          onUnpin={handleUnpin}
        />
      )}

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex flex-col flex-1"
        style={{ gap: 16, overflowY: "auto", minHeight: 0, padding: "0 10px" }}
      >
        {isLoadingOlderMessages && (
          <div className="flex justify-center" style={{ padding: 12 }}>
            <span
              className="text-h3"
              style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
            >
              Chargement...
            </span>
          </div>
        )}
        {messageGroups.map((group, gi) => {
          const prev = gi > 0 ? messageGroups[gi - 1] : null;
          const showDay = !prev || msgDayKey(prev.messages[0]) !== msgDayKey(group.messages[0]);
          return (
            <React.Fragment key={group.messages[0].id}>
              {showDay && <DaySeparator iso={group.messages[0].created_at || group.messages[0].timestamp} />}
              <MessageGroup group={group} />
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {typingNames.length > 0 && (
        <div
          className="flex items-center"
          style={{ padding: "4px 16px", gap: 6 }}
        >
          <span className="inline-flex items-center" style={{ gap: 3 }}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="inline-block rounded-full"
                style={{
                  width: 6,
                  height: 6,
                  background: "#A1A1A1",
                  animation: `waTyping 1.2s ease-in-out ${i * 0.15}s infinite`,
                }}
              />
            ))}
            <style>{`
              @keyframes waTyping {
                0%   { opacity: 0.3; transform: translateY(0); }
                25%  { opacity: 1;   transform: translateY(-3px); }
                50%  { opacity: 0.3; transform: translateY(0); }
                100% { opacity: 0.3; transform: translateY(0); }
              }
            `}</style>
          </span>
          <span
            style={{
              fontSize: 12,
              color: "#A1A1A1",
              fontFamily: "Poppins",
            }}
          >
            {typingNames.join(", ")}{" "}
            {typingNames.length === 1 ? "ecrit" : "ecrivent"}...
          </span>
        </div>
      )}

      {/* Modals */}
      {showAddMembers && selectedDiscussionId && (
        <AddMembersModal
          onClose={() => setShowAddMembers(false)}
          discussionId={selectedDiscussionId}
        />
      )}
      {/* Input */}
      <ChatInput />
    </div>
  );
}

// --- Chat panel router: entity-linked → ChatPanel, otherwise flat ---

function ChatPanelSkeleton() {
  return (
    <div className="flex-1 flex flex-col" style={{ background: "#FAFAFA" }}>
      {/* Header skeleton */}
      <div className="flex flex-row items-center px-5 py-3 border-b border-neutral-200 bg-white" style={{ gap: 12 }}>
        <div className="w-10 h-10 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="h-4 w-36 rounded bg-neutral-200 animate-pulse" />
          <div className="h-3 w-20 rounded bg-neutral-100 animate-pulse" />
        </div>
      </div>

      {/* Messages skeleton */}
      <div className="flex-1 flex flex-col px-5 py-4" style={{ gap: 16 }}>
        {/* Left-aligned messages */}
        <div className="flex flex-row items-end gap-2" style={{ maxWidth: "60%" }}>
          <div className="w-7 h-7 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
          <div className="flex flex-col gap-1.5">
            <div className="h-10 w-52 rounded-xl bg-neutral-200 animate-pulse" />
            <div className="h-8 w-36 rounded-xl bg-neutral-200 animate-pulse" />
          </div>
        </div>

        {/* Right-aligned message */}
        <div className="flex flex-row justify-end">
          <div className="h-10 w-44 rounded-xl bg-neutral-200 animate-pulse" />
        </div>

        {/* Left-aligned message */}
        <div className="flex flex-row items-end gap-2" style={{ maxWidth: "60%" }}>
          <div className="w-7 h-7 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
          <div className="h-14 w-64 rounded-xl bg-neutral-200 animate-pulse" />
        </div>

        {/* Right-aligned messages */}
        <div className="flex flex-col items-end gap-1.5">
          <div className="h-8 w-40 rounded-xl bg-neutral-200 animate-pulse" />
          <div className="h-10 w-56 rounded-xl bg-neutral-200 animate-pulse" />
        </div>

        {/* Left-aligned message */}
        <div className="flex flex-row items-end gap-2" style={{ maxWidth: "60%" }}>
          <div className="w-7 h-7 rounded-full bg-neutral-200 animate-pulse flex-shrink-0" />
          <div className="h-8 w-48 rounded-xl bg-neutral-200 animate-pulse" />
        </div>
      </div>

      {/* Input skeleton */}
      <div className="px-5 py-3 border-t border-neutral-200 bg-white">
        <div className="h-10 w-full rounded-xl bg-neutral-100 animate-pulse" />
      </div>
    </div>
  );
}

function ChatPanelRouter({ onToggleInfo, initialTab = "client" }: { onToggleInfo: () => void; initialTab?: "client" | "privee" }) {
  const selectedDiscussionId = useMessagerieStore((s) => s.selectedDiscussionId);
  const discussions = useMessagerieStore((s) => s.discussions);
  const members = useMessagerieStore((s) => s.members);
  const isLoadingDiscussions = useMessagerieStore((s) => s.isLoadingDiscussions);

  const discussion = discussions.find((d) => d.id === selectedDiscussionId);

  // Check if this is an entity-linked (dossier/ordre) discussion
  const isEntityLinked = discussion && (discussion.dossier_id || discussion.ordre_id);

  // Find sibling internal discussion — first check loaded discussions, then fetch
  const [fetchedInternalId, setFetchedInternalId] = useState<string | undefined>(undefined);
  const internalDiscussion = useMemo(() => {
    if (!discussion || !isEntityLinked) return undefined;
    const entityId = discussion.ordre_id || discussion.dossier_id || discussion.project_id;
    return discussions.find((d) =>
      d.type === "internal" &&
      d.id !== discussion.id &&
      (d.ordre_id === entityId || d.dossier_id === entityId || d.project_id === entityId)
    );
  }, [discussion, discussions, isEntityLinked]);

  // If internal discussion not in local list, fetch entity to get its internal_discussion_id
  useEffect(() => {
    if (!discussion || !isEntityLinked || internalDiscussion) {
      setFetchedInternalId(undefined);
      return;
    }
    const entityId = discussion.ordre_id || discussion.dossier_id;
    const collection = discussion.ordre_id ? "ordres" : "dossiers";
    if (!entityId) return;
    // Fetch the entity to get internal_discussion_id
    import("@services/api/client").then(({ default: api }) => {
      api.get(`/${collection}/${entityId}`).then((resp: any) => {
        const intId = resp.data?.internal_discussion_id;
        if (intId) setFetchedInternalId(intId);
      }).catch(() => {});
    });
  }, [discussion?.id, isEntityLinked, internalDiscussion]);

  const resolvedInternalId = internalDiscussion?.id || fetchedInternalId;

  const chatMembers = useMemo(() => {
    if (!discussion) return [];
    return members
      .filter((m) => discussion.members.includes(m.id))
      .map((m) => ({ name: m.name, photo_url: m.photo_url, avatar_seed: m.avatar_seed }));
  }, [discussion, members]);

  const onlineCount = useMemo(() => {
    if (!discussion) return 0;
    return members.filter((m) => discussion.members.includes(m.id) && m.online).length;
  }, [discussion, members]);

  if (!discussion) {
    // Show skeleton while loading, placeholder when loaded with nothing selected
    if (isLoadingDiscussions && discussions.length === 0) {
      return <ChatPanelSkeleton />;
    }
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: "#FAFAFA" }}>
        <span className="text-h1" style={{ fontFamily: "Poppins", fontWeight: 500, color: "#A1A1A1" }}>
          Sélectionnez une discussion
        </span>
      </div>
    );
  }

  if (isEntityLinked) {
    return (
      <div className="flex-1 flex flex-col" style={{ minWidth: 0 }}>
        <EntityChatPanel
          discussionId={discussion.id}
          internalDiscussionId={resolvedInternalId}
          entityType={discussion.ordre_id ? "ordre" : "dossier"}
          entityId={discussion.ordre_id || discussion.dossier_id}
          entityName={discussionTitle(discussion)}
          entityLinks={{ ordreId: discussion.ordre_id, dossierId: discussion.dossier_id }}
          members={chatMembers}
          onlineCount={onlineCount}
          clientChannel={discussion.channel}
          whatsappLinked={!!discussion.whatsapp_phone}
          whatsappProvider={useDataStore.getState().orgData?.whatsapp?.provider || "wasender"}
          initialTab={initialTab}
          onToggleInfo={onToggleInfo}
        />
      </div>
    );
  }

  return <FlatChatPanel onToggleInfo={onToggleInfo} />;
}

// --- Main Screen ---

export default function MessagerieScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const [chatInitialTab, setChatInitialTab] = useState<"client" | "privee">("client");
  const selectedDiscussionId = useMessagerieStore(
    (s) => s.selectedDiscussionId,
  );
  const allMessages = useMessagerieStore((s) => s.messages);
  const discussions = useMessagerieStore((s) => s.discussions);
  const members = useMessagerieStore((s) => s.members);
  const init = useMessagerieStore((s) => s.init);
  const activeTab = useMessagerieStore((s) => s.activeTab);
  const setActiveTab = useMessagerieStore((s) => s.setActiveTab);
  const setSelected = useMessagerieStore((s) => s.setSelectedDiscussion);
  const fetchDiscussions = useMessagerieStore((s) => s.fetchDiscussions);
  const isLoadingDiscussions = useMessagerieStore((s) => s.isLoadingDiscussions);

  const discussion = discussions.find((d) => d.id === selectedDiscussionId);
  const isEntityLinked = discussion && (discussion.dossier_id || discussion.ordre_id);
  const messages = selectedDiscussionId
    ? allMessages[selectedDiscussionId] || []
    : [];
  const chatMembers = discussion
    ? members.filter((m) => discussion.members.includes(m.id))
    : [];

  // Init store on mount (WS stays connected app-wide)
  useEffect(() => {
    init();
    return () => {
      // Clear selection so WS handler knows we're not viewing any discussion
      useMessagerieStore.setState({ selectedDiscussionId: null, viewingDiscussionId: null });
    };
  }, []);

  // Handle deep-link from notification: ?tab=ordres&discussion=abc123
  const pendingDiscussionRef = useRef<string | null>(null);
  useEffect(() => {
    const tab = searchParams.get("tab") as DiscussionTab | null;
    const discId = searchParams.get("discussion");
    if (!tab && !discId) return;

    if (tab) setActiveTab(tab);
    pendingDiscussionRef.current = discId;
    setSearchParams({}, { replace: true });
  }, []); // only on mount

  // Resolve a discussion for selection — if internal, find and select its client sibling instead
  const resolveAndSelect = useCallback(async (disc: Discussion) => {
    if (disc.type === "internal") {
      // Internal discussion: find the client sibling
      setChatInitialTab("privee");
      const entityId = disc.ordre_id || disc.dossier_id || disc.project_id;
      if (entityId) {
        const clientSibling = useMessagerieStore.getState().discussions.find((d) =>
          d.type !== "internal" && (d.ordre_id === entityId || d.dossier_id === entityId || d.project_id === entityId)
        );
        if (clientSibling) {
          setSelected(clientSibling.id);
          return;
        }
      }
      // Fallback: inject the internal disc and select it (rare edge case)
      useMessagerieStore.setState((s) => ({
        discussions: [disc, ...s.discussions],
      }));
      setSelected(disc.id);
    } else {
      setChatInitialTab("client");
      setSelected(disc.id);
    }
  }, [setSelected]);

  // Once discussions load, select the pending one
  useEffect(() => {
    const discId = pendingDiscussionRef.current;
    if (!discId || discussions.length === 0) return;
    pendingDiscussionRef.current = null;

    const found = discussions.find((d) => d.id === discId);
    if (found) {
      resolveAndSelect(found);
      setDeepLinkLoading(false);
      return;
    }

    // Not in the list — fetch the single discussion directly (skip full refetch)
    setDeepLinkLoading(true);
    messagerieApi.getDiscussion(discId).then((disc) => {
      if (disc.type === "internal") {
        setChatInitialTab("privee");
        const entityId = disc.ordre_id || disc.dossier_id || disc.project_id;
        if (entityId) {
          const clientSib = useMessagerieStore.getState().discussions.find((d) =>
            d.type !== "internal" && (d.ordre_id === entityId || d.dossier_id === entityId || d.project_id === entityId)
          );
          if (clientSib) {
            setSelected(clientSib.id);
            return;
          }
        }
      } else {
        setChatInitialTab("client");
      }
      // Inject into list and select
      useMessagerieStore.setState((s) => ({
        discussions: [disc, ...s.discussions],
      }));
      setSelected(disc.id);
    }).catch(() => {}).finally(() => setDeepLinkLoading(false));
  }, [discussions]);

  // Close info panel and reset chat tab when switching discussions manually
  const prevDiscIdRef = useRef(selectedDiscussionId);
  useEffect(() => {
    setShowInfoPanel(false);
    // Reset initialTab to client when user manually selects a different discussion
    // (not from deep-link — deep-link sets chatInitialTab before setSelected)
    if (prevDiscIdRef.current !== null && selectedDiscussionId !== prevDiscIdRef.current) {
      // Only reset if not in deep-link flow
      if (!pendingDiscussionRef.current && !deepLinkLoading) {
        setChatInitialTab("client");
      }
    }
    prevDiscIdRef.current = selectedDiscussionId;
  }, [selectedDiscussionId]);

  return (
    <div className="flex flex-row h-screen overflow-hidden relative">
      {(deepLinkLoading || (isLoadingDiscussions && discussions.length === 0)) && <LoadingBar variant="fixed" />}
      {deepLinkLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.6)", backdropFilter: "blur(2px)" }}>
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full" style={{ width: 32, height: 32, border: "3px solid #E5E7EB", borderTopColor: "#6366f1" }} />
            <span style={{ fontFamily: "Poppins", fontSize: 13, fontWeight: 500, color: "#6B7280" }}>Chargement de la conversation...</span>
          </div>
        </div>
      )}
      <DiscussionsPanel />
      <ChatPanelRouter onToggleInfo={() => setShowInfoPanel((v) => !v)} initialTab={chatInitialTab} />
      {showInfoPanel && discussion && (
        <ConvInfoPanel
          onClose={() => setShowInfoPanel(false)}
          discussion={discussion}
          chatMembers={chatMembers}
          messages={messages}
        />
      )}
    </div>
  );
}
